import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { IconTagInput } from '../IconTagInput';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import rentalTemplatePresetsData from '../../schemas/rentalTemplatePresets.json';
import { 
  X, Camera, Image as ImageIcon, Loader2, CheckCircle, ChevronRight, 
  RotateCcw, Package, Briefcase, Calendar, Key, RefreshCw,
  ChevronDown, ChevronUp, MapPin, Clock, AlertCircle, Crop, ArrowLeft,
  Settings2, Layers, Plus, Trash2, FileText, Search, Link2, Truck,
  CalendarOff, ShieldCheck, ScrollText, Globe, Tag, CheckSquare, XCircle,
  // Professional Services Icons
  Calculator, Building2, Sparkles, PiggyBank, Shield, Scale, 
  TrendingUp, Wrench, Palette, Printer, Home, PenTool, Code, 
  Layout, Monitor, Compass, Users, BadgeDollarSign, HelpCircle,
  Stethoscope, GraduationCap, Megaphone, Upload
} from 'lucide-react';
import LiteAvailabilityScheduler from './LiteAvailabilityScheduler';
import VariantManagerV2 from '../VariantManagerV2';
import CustomOptionsConfig from '../CustomOptionsConfig';
import HolidayCalendar from '../HolidayCalendar';

// iter-331 — Legal disclosure field options
const COUNTRY_OF_ORIGIN_OPTIONS = [
  'India', 'China', 'USA', 'United Kingdom', 'Germany', 'Japan', 'South Korea',
  'Vietnam', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Thailand', 'Indonesia', 'Other',
];
const LICENSE_TYPE_OPTIONS = ['FSSAI', 'BIS', 'CDSCO', 'AGMARK', 'ISI', 'Other'];

// ============ PROFESSIONAL SERVICES DEFAULT ICONS ============
// Maps subcategory names to lucide-react icons for Professional Services
const PROFESSIONAL_SERVICES_ICONS = {
  'Accountant': Calculator,
  'Architect': Building2,
  'Astrologer': Sparkles,
  'Financial Advisor': PiggyBank,
  'Insurance Advisor': Shield,
  'Lawyers': Scale,
  'Marketing Consultant': Megaphone,
  'Engineers': Wrench,
  'Interior Designer': Palette,
  'Printers': Printer,
  'Real Estate Agent': Home,
  'Graphic Designer': PenTool,
  'Web/App Developer': Code,
  'UX/UI Designer': Layout,
  'Software Developer': Monitor,
  'Vastu Consultant': Compass,
  'Business Consultant': Users,
  'Sales Consultant': BadgeDollarSign,
  'Doctor': Stethoscope,
  'Tutor': GraduationCap,
  'Others': HelpCircle
};

// Generate SVG data URL for a professional service icon
const generateProfessionalServiceIcon = (subcategoryName, primaryColor = '#6366f1') => {
  const IconComponent = PROFESSIONAL_SERVICES_ICONS[subcategoryName] || HelpCircle;
  
  // Create SVG string with the icon
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#f8fafc"/>
          <stop offset="100%" style="stop-color:#e2e8f0"/>
        </linearGradient>
      </defs>
      <rect width="400" height="400" fill="url(#bg)"/>
      <circle cx="200" cy="200" r="120" fill="${primaryColor}" opacity="0.1"/>
      <circle cx="200" cy="200" r="80" fill="${primaryColor}" opacity="0.15"/>
    </svg>
  `;
  
  return `data:image/svg+xml,${encodeURIComponent(svgString)}`;
};

// Check if category is Professional Services
const isProfessionalServicesCategory = (categoryName) => {
  if (!categoryName) return false;
  const normalized = categoryName.toLowerCase().replace(/[^a-z]/g, '');
  return normalized.includes('professionalservices') || normalized === 'professionalservices';
};

// ============ HELPER: Create cropped image from canvas ============
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new window.Image();
  image.src = imageSrc;
  
  return new Promise((resolve, reject) => {
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set output size (800x800 for good quality)
      const outputSize = 800;
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      // Draw the cropped portion of the image
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        outputSize,
        outputSize
      );
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
            const preview = canvas.toDataURL('image/jpeg', 0.9);
            resolve({ file, preview });
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        0.9
      );
    };
    image.onerror = () => reject(new Error('Failed to load image'));
  });
};

// ============ IMAGE CROPPER COMPONENT (Gesture-based) ============
const ImageCropper = ({ image, onCrop, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleDone = async () => {
    if (!croppedAreaPixels) return;
    
    setIsProcessing(true);
    try {
      const { file, preview } = await createCroppedImage(image, croppedAreaPixels);
      onCrop(file, preview);
    } catch (error) {
      console.error('Crop failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white shrink-0">
        <button type="button" onClick={onCancel} className="p-2 -ml-2">
          <X size={24} />
        </button>
        <span className="font-medium">Crop Image</span>
        <button 
          type="button" 
          onClick={handleDone}
          disabled={isProcessing}
          className="px-4 py-2 bg-indigo-600 rounded-lg font-medium disabled:opacity-50"
        >
          {isProcessing ? 'Processing...' : 'Done'}
        </button>
      </div>
      
      {/* Crop Area - Takes most of the screen */}
      <div className="flex-1 relative" style={{ touchAction: 'none' }}>
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          showGrid={true}
          objectFit="contain"
          style={{
            containerStyle: {
              width: '100%',
              height: '100%',
              backgroundColor: '#000'
            },
            cropAreaStyle: {
              border: '2px solid #fff',
              borderRadius: '8px'
            }
          }}
        />
      </div>
      
      {/* Footer with instructions */}
      <div className="p-4 bg-black/80 text-white text-center space-y-3 shrink-0">
        <p className="text-sm text-gray-300">
          📱 Pinch to zoom • Drag to position
        </p>
        <button 
          type="button"
          onClick={handleReset}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg flex items-center justify-center gap-2 mx-auto hover:bg-gray-700 transition-colors"
        >
          <RotateCcw size={16} />
          Reset
        </button>
      </div>
    </div>
  );
};

// ============ LISTING TYPE CONFIG ============
const LISTING_TYPES = [
  { 
    id: 'product', 
    label: 'Product', 
    icon: Package,
    priceLabel: 'Price (₹)',
    showStock: true,
    showShipping: true,
    showAvailability: false,
    showLocation: false,
    defaults: { requires_shipping: true, track_inventory: true }
  },
  { 
    id: 'service', 
    label: 'Service', 
    icon: Briefcase,
    priceLabel: 'Price (₹)',
    showStock: false,
    showShipping: false,
    showAvailability: true,
    showLocation: true,
    showDuration: true,
    // Default to enquiry (doesn't require KYC), availability still collected for reference
    defaults: { booking_type: 'enquiry', requires_shipping: false }
  },
  { 
    id: 'event', 
    label: 'Event', 
    icon: Calendar,
    priceLabel: 'Ticket Price (₹)',
    pricePlaceholder: '0 for free event',
    showStock: false,
    showShipping: false,
    showDate: true,
    showAvailability: false,
    showLocation: false,
    defaults: { registration_type: 'free', requires_shipping: false }
  },
  { 
    id: 'rental', 
    label: 'Rental', 
    icon: Key,
    priceLabel: 'Rate (₹)',
    showStock: false,
    showShipping: false,
    showRentalPeriod: true,
    showAvailability: true,
    showRentalLocation: true,
    showSecurityDeposit: true,
    defaults: { pricing_type: 'daily', instant_booking: false }
  },
  { 
    id: 'subscription', 
    label: 'Subscription', 
    icon: RefreshCw,
    priceLabel: 'Monthly Price (₹)',
    showStock: false,
    showShipping: true,
    showAvailability: false,
    showLocation: false,
    defaults: { auto_renewal: true, requires_shipping: true }
  }
];

// Service location options
const SERVICE_LOCATIONS = [
  { value: 'online', label: 'Online / Virtual', icon: '💻' },
  { value: 'provider_location', label: 'At My Location', icon: '🏠' },
  { value: 'customer_location', label: 'I Visit Customer', icon: '🚗' }
];

// Indian States for serviceable area selection
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  'Andaman & Nicobar', 'Dadra & Nagar Haveli', 'Daman & Diu', 'Lakshadweep'
];

// ============ MAIN COMPONENT ============

// ---- TagInput: simple tag-based input for inclusions/exclusions ----
const TagInput = ({ tags = [], onChange, placeholder, testId }) => {
  const [inputVal, setInputVal] = useState('');
  const addTag = () => {
    const trimmed = inputVal.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
      setInputVal('');
    }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          data-testid={testId}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); }}}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
        />
        <button type="button" onClick={addTag} disabled={!inputVal.trim()}
          className="px-3 py-2 bg-indigo-600 text-white text-xs rounded-lg disabled:bg-gray-300">
          <Plus size={14} />
        </button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map((tag, i) => (
            <span key={tag.id || tag.name || `${tag.title || "tag"}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">
              {tag}
              <button type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
                className="hover:text-red-600"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const LiteAddItemOverlay = ({ 
  isOpen, 
  onClose, 
  intent = 'product', 
  onSuccess,
  // Edit mode props
  editMode = false,
  listingId = null,
  initialData = null,
  // Lite Mode page props (when rendered as a page, not overlay)
  isLiteMode = false,
  initialType = null
}) => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // iter-355 — Vendor's last-used license numbers/photos (parity with the
  // desktop ComplianceCard's prefill, iter-349) — fetched once, used to
  // pre-fill any newly-added license row for a type the vendor already has.
  const [defaultLicenses, setDefaultLicenses] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/listings/defaults/licenses');
        setDefaultLicenses(res?.default_licenses || {});
      } catch (e) {
        // Non-fatal — form still works without prefill
      }
    })();
  }, [apiCall]);
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingListing, setIsLoadingListing] = useState(false);
  const [listingType, setListingType] = useState(
    initialData?.listing_type || initialType || (intent === 'services' ? 'service' : 'product')
  );
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    stock: '1',
    category: '',
    subcategory: '',
    requires_shipping: true,
    // iter-331 — Legal disclosure fields
    country_of_origin: 'India',
    licenses_certifications: [],
    excluded_license_types: [],
    // iter-332 — Shelf-Life/Expiry (perishables) + Warranty/Guarantee (non-perishables)
    shelf_life_days: '',
    expiry_date: '',
    freshness_guarantee_days: '',
    warranty_duration_value: '',
    warranty_duration_unit: 'months',
    warranty_assurance_type: 'manufacturer_warranty',
    // Weight fields
    weight: '',
    weight_unit: 'kg',
    // Tax fields (moved from advanced for visibility)
    charge_tax: false,
    // Digital product fields
    product_type: 'physical',
    digital_download_url: '',
    // Event fields
    event_date: '',
    event_time: '',
    // Rental fields
    rental_period: 'daily',
    security_deposit: '0',
    rental_address: '',
    rental_city: '',
    rental_pincode: '',
    min_rental_period: '1',
    min_rental_unit: 'days',
    // Service fields
    booking_type: 'enquiry',
    service_location: 'provider_location',
    service_duration: '60',
    service_address: '',
    service_city: '',
    meeting_link: '',
    travel_radius: '',
    // Inclusions & Exclusions
    inclusions: [],
    exclusions: [],
    // Service - Booking Mode (instant/request/enquiry)
    bookingMode: 'instant',
    // Service & Rental - Travel Booking Settings
    allow_travel_booking: false,
    travel_charge_per_km: '',
    min_travel_fee: '',
    max_travel_distance_km: '',
    // Rental - Booking Mode
    rental_booking_mode: 'instant',
    // Rental template (derived from subcategory preset)
    rental_template: 'rental_inventory',
    // Venue-specific location fields (for venue_booking template)
    venue_nearest_landmark: '',
    venue_nearest_transport: '',
    venue_parking_available: false,
    venue_google_maps_link: '',
    // Event - additional fields
    organizer_name: '',
    is_recurring: false,
    recurrence_frequency: 'weekly',
    event_inclusions: [],
    event_exclusions: [],
    // Subscription - Delivery Coverage
    delivery_type: 'area',
    delivery_center_address: '',
    delivery_radius: '',
    delivery_pincodes: '',
    // Service / Rental - Serviceable Area (GST compliance geofencing)
    serviceableArea: {
      coverage_type: 'all_india',
      states: [],
      cities: '',
      pincodes: [],
      countries: [],
      radius_km: ''
    },
  });
  
  // Ref to track initial edit load (prevents category/subcategory reset)
  const editInitialLoadRef = useRef(false);
  
  // Availability state (for services and rentals)
  const [availability, setAvailability] = useState({
    business_hours: [],
    slot_duration: 60,
    bookings_per_slot: 1,
    buffer_between_bookings: 0
  });
  
  // Image state - Updated to support multiple images with background upload
  // Each image: { id, file, preview, rawImage, uploadStatus: 'pending'|'uploading'|'completed'|'failed', uploadedUrl: string|null }
  const [images, setImages] = useState([]);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperImageIndex, setCropperImageIndex] = useState(null);
  
  // Background upload queue ref to track ongoing uploads
  const uploadQueueRef = useRef(new Map());
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Advanced Details state
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Advanced form data
  const [advancedData, setAdvancedData] = useState({
    // Tax fields
    hsn_code: '',
    tax_rate: '',
    taxable: true,
    // SEO fields
    meta_title: '',
    meta_description: '',
    slug: '',
    // Variants
    has_variants: false,
    variants: [],
    variantCombinations: [],
    // Custom Options
    has_custom_options: false,
    customOptionsConfig: { options: [], hasOptions: false },
    // Product: Serviceable Areas
    serviceable_type: 'country',
    serviceable_states: [],
    international_regions: [],
    // Product: Return & Cancellation Policy
    cancellation_allowed: true,
    cancellation_window: '24hrs',
    cancellation_timing: 'before_dispatch',
    return_policy: 'return_exchange',
    return_exchange_window: '7',
    // Service: Logistics Charges
    has_logistics_charges: false,
    logistics_charge_type: 'fixed',
    logistics_base_charge: '',
    logistics_per_km_rate: '',
    logistics_free_radius: '5',
    logistics_description: '',
    // Service: Holiday Calendar
    holidays: [],
    // Service: Service Guarantee
    service_guarantee_enabled: false,
    service_guarantee_type: 'full',
    service_guarantee_refund_pct: '',
    service_guarantee_days: '',
    // Service: Policies & Terms
    svc_cancellation_allowed: false,
    svc_cancellation_window: '24hrs',
    svc_cancellation_timing: 'before_service_date',
    svc_terms: '',
    svc_cancellation_details: '',
  });

  // Get current listing type config
  const typeConfig = LISTING_TYPES.find(t => t.id === listingType) || LISTING_TYPES[0];

  // ============ Derive rental_template from subcategory for rentals ============
  useEffect(() => {
    if (listingType !== 'rental') return;
    // formData.subcategory stores the subcategory UUID from the API.
    // rentalTemplatePresets keys use subcategory NAMES (e.g. "hall / venue booking").
    // So we look up the name from the loaded subcategories array first.
    const selectedSub = subcategories.find(s => s.id === formData.subcategory);
    const subName = (selectedSub?.name || formData.subcategory || '').toLowerCase();
    const presets = rentalTemplatePresetsData?.subcategoryPresets || {};
    const matched = Object.entries(presets).find(([key]) => key.toLowerCase() === subName);
    const template = matched ? matched[1].rentalTemplate : 'rental_inventory';
    setFormData(prev => {
      if (prev.rental_template === template) return prev;
      // For venue_booking, default booking mode to request
      const updates = { rental_template: template };
      if (template === 'venue_booking') updates.rental_booking_mode = 'request';
      return { ...prev, ...updates };
    });
  }, [listingType, formData.subcategory, subcategories]);
  useEffect(() => {
    if (editMode) return; // Only for new listings
    try {
      // Universal cross-type policy defaults (lowest priority baseline)
      const universal = JSON.parse(localStorage.getItem('vendor_universal_policies') || '{}');

      if (listingType === 'product') {
        const saved = JSON.parse(localStorage.getItem('vendor_product_defaults') || '{}');
        setAdvancedData(prev => ({
          ...prev,
          serviceable_type: saved.serviceable_type || prev.serviceable_type,
          serviceable_states: saved.serviceable_states || prev.serviceable_states,
          international_regions: saved.international_regions || prev.international_regions,
          cancellation_allowed: saved.cancellation_allowed ?? universal.cancellation_allowed ?? prev.cancellation_allowed,
          cancellation_window: saved.cancellation_window || universal.cancellation_window || prev.cancellation_window,
          cancellation_timing: saved.cancellation_timing || universal.cancellation_timing || prev.cancellation_timing,
          return_policy: saved.return_policy || prev.return_policy,
          return_exchange_window: saved.return_exchange_window || prev.return_exchange_window,
        }));
      } else if (listingType === 'service') {
        const saved = JSON.parse(localStorage.getItem('vendor_service_defaults') || '{}');
        setAdvancedData(prev => ({
          ...prev,
          service_guarantee_enabled: saved.service_guarantee_enabled ?? prev.service_guarantee_enabled,
          service_guarantee_type: saved.service_guarantee_type || prev.service_guarantee_type,
          service_guarantee_refund_pct: saved.service_guarantee_refund_pct || prev.service_guarantee_refund_pct,
          service_guarantee_days: saved.service_guarantee_days || prev.service_guarantee_days,
          svc_cancellation_allowed: saved.svc_cancellation_allowed ?? universal.cancellation_allowed ?? prev.svc_cancellation_allowed,
          svc_cancellation_window: saved.svc_cancellation_window || universal.cancellation_window || prev.svc_cancellation_window,
          svc_cancellation_timing: saved.svc_cancellation_timing || prev.svc_cancellation_timing,
          svc_terms: saved.svc_terms || universal.terms || prev.svc_terms,
          svc_cancellation_details: saved.svc_cancellation_details || universal.cancellation_details || prev.svc_cancellation_details,
        }));
        // Pre-populate serviceableArea from saved defaults
        if (saved.serviceableArea) {
          setFormData(prev => ({ ...prev, serviceableArea: saved.serviceableArea }));
        }
      } else if (listingType === 'rental') {
        const saved = JSON.parse(localStorage.getItem('vendor_rental_defaults') || '{}');
        if (Object.keys(saved).length > 0 || Object.keys(universal).length > 0) {
          setAdvancedData(prev => ({
            ...prev,
            // rental uses svc_ policy fields in advanced
            svc_cancellation_allowed: saved.cancellation_allowed ?? universal.cancellation_allowed ?? prev.svc_cancellation_allowed,
            svc_cancellation_window: saved.cancellation_window || universal.cancellation_window || prev.svc_cancellation_window,
            svc_terms: saved.terms || universal.terms || prev.svc_terms,
            svc_cancellation_details: saved.damage_policy || universal.cancellation_details || prev.svc_cancellation_details,
          }));
        }
      } else if (listingType === 'event') {
        const saved = JSON.parse(localStorage.getItem('vendor_event_defaults') || '{}');
        if (Object.keys(saved).length > 0 || Object.keys(universal).length > 0) {
          setAdvancedData(prev => ({
            ...prev,
            svc_cancellation_allowed: saved.cancellation_allowed ?? universal.cancellation_allowed ?? prev.svc_cancellation_allowed,
            svc_cancellation_window: saved.cancellation_window || universal.cancellation_window || prev.svc_cancellation_window,
            svc_terms: saved.terms || universal.terms || prev.svc_terms,
          }));
        }
      } else if (listingType === 'subscription') {
        const saved = JSON.parse(localStorage.getItem('vendor_subscription_defaults') || '{}');
        if (Object.keys(saved).length > 0 || Object.keys(universal).length > 0) {
          setAdvancedData(prev => ({
            ...prev,
            svc_cancellation_allowed: saved.cancellation_allowed ?? universal.cancellation_allowed ?? prev.svc_cancellation_allowed,
            svc_cancellation_window: saved.cancellation_window || universal.cancellation_window || prev.svc_cancellation_window,
            svc_terms: saved.terms || universal.terms || prev.svc_terms,
          }));
        }
      }
    } catch (e) { /* ignore parse errors */ }
  }, [editMode, listingType]);

  // Load listing data in edit mode
  useEffect(() => {
    if (editMode && listingId && isOpen) {
      loadListingData();
    }
  }, [editMode, listingId, isOpen]);

  const loadListingData = async () => {
    if (!listingId) return;
    
    setIsLoadingListing(true);
    try {
      const listing = await apiCall(`/api/listings/${listingId}`);
      
      if (listing) {
        // Set listing type (fixed in edit mode)
        setListingType(listing.listing_type || 'product');
        
        const d = listing.data || {};
        
        // Resolve category: try data.categoryId first, then category name
        const resolvedCategory = d.categoryId || d.category_id || listing.category || '';
        const resolvedSubcategory = d.subcategoryId || d.subcategory_id || listing.subcategory || '';
        
        // Mark that we're doing initial edit load (prevents category/subcategory reset)
        editInitialLoadRef.current = true;
        
        // Set form data - read from data dict (regular mode format) with fallbacks
        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          price: (d.price ?? listing.price ?? '').toString(),
          stock: (d.inventory ?? d.stock ?? d.stock_quantity ?? listing.stock ?? '1').toString(),
          category: resolvedCategory,
          subcategory: resolvedSubcategory,
          requires_shipping: d.requiresShipping ?? d.requires_shipping ?? listing.logistics?.requires_shipping ?? true,
          // iter-331 — Legal disclosure fields
          country_of_origin: listing.country_of_origin || 'India',
          licenses_certifications: listing.licenses_certifications || [],
          excluded_license_types: listing.excluded_license_types || [],
          // iter-332 — Shelf-Life/Expiry + Warranty/Guarantee
          shelf_life_days: listing.shelf_life?.shelf_life_days ?? '',
          expiry_date: listing.shelf_life?.expiry_date || '',
          freshness_guarantee_days: listing.shelf_life?.min_freshness_guarantee_days ?? '',
          warranty_duration_value: listing.warranty?.duration_value ?? '',
          warranty_duration_unit: listing.warranty?.duration_unit || 'months',
          warranty_assurance_type: listing.warranty?.assurance_type || 'manufacturer_warranty',
          // Weight fields
          weight: (d.weight ?? '').toString(),
          weight_unit: d.weightUnit || d.weight_unit || 'kg',
          // Tax fields
          charge_tax: d.chargeTax ?? d.charge_tax ?? false,
          // Digital product fields
          product_type: d.productType || d.product_type || listing.product_type || 'physical',
          digital_download_url: d.digitalDownloadUrl || d.digital_download_url || listing.digital_delivery?.download_url || '',
          // Event fields
          event_date: d.event_date || '',
          event_time: d.event_time || '',
          // Rental fields
          rental_period: d.rental_period || 'daily',
          security_deposit: (d.security_deposit ?? '0').toString(),
          rental_address: d.rental_address || '',
          rental_city: d.rental_city || '',
          rental_pincode: d.rental_pincode || '',
          min_rental_period: (d.min_rental_period ?? '1').toString(),
          min_rental_unit: d.min_rental_unit || 'days',
          // Service fields
          booking_type: d.booking_type || listing.booking_type || 'enquiry',
          service_location: d.service_location || d.locationType || listing.locationType || 'provider_location',
          service_duration: (d.service_duration || d.duration || '60').toString(),
          service_address: d.service_address || d.serviceAddress || '',
          service_city: d.service_city || '',
          meeting_link: d.meeting_link || d.meetingLink || '',
          travel_radius: (d.travel_radius || d.travelRadius || '').toString(),
          // Inclusions & Exclusions
          inclusions: d.inclusions || listing.inclusions || [],
          exclusions: d.exclusions || listing.exclusions || [],
          // Service / Rental - Serviceable Area
          serviceableArea: d.serviceableArea || d.data?.serviceableArea || {
            coverage_type: 'all_india', states: [], cities: '', pincodes: [], countries: [], radius_km: ''
          },
          // Service - Booking Mode
          bookingMode: d.bookingMode || (d.booking_type === 'enquiry' ? 'enquiry' : d.booking_type === 'request' ? 'request' : 'instant'),
          // Service & Rental - Travel Booking
          allow_travel_booking: d.travelBooking?.allowTravelBooking ?? d.allow_travel_booking ?? false,
          travel_charge_per_km: (d.travelBooking?.travelChargePerKm ?? d.travel_charge_per_km ?? '').toString(),
          min_travel_fee: (d.travelBooking?.minimumTravelFee ?? d.min_travel_fee ?? '').toString(),
          max_travel_distance_km: (d.travelBooking?.maxTravelDistanceKm ?? d.max_travel_distance_km ?? '').toString(),
          // Rental - Booking Mode
          rental_booking_mode: d.bookingMode || 'instant',
          // Rental template
          rental_template: d.rentalTemplate || d.rental_template || 'rental_inventory',
          // Venue-specific fields
          venue_nearest_landmark: d.nearestLandmark || d.venue_nearest_landmark || '',
          venue_nearest_transport: d.nearestTransport || d.venue_nearest_transport || '',
          venue_parking_available: d.parkingAvailable ?? d.venue_parking_available ?? false,
          venue_google_maps_link: d.googleMapsLink || d.venue_google_maps_link || '',
          // Event - additional fields
          organizer_name: d.organizerName || d.organizer_name || '',
          is_recurring: d.isRecurring ?? d.is_recurring ?? false,
          recurrence_frequency: d.recurrenceFrequency || d.recurrence_frequency || 'weekly',
          event_inclusions: d.inclusions || listing.inclusions || [],
          event_exclusions: d.exclusions || listing.exclusions || [],
          // Subscription - Delivery Coverage
          delivery_type: d.deliveryType || d.delivery_type || 'area',
          delivery_center_address: d.deliveryCenterAddress || d.delivery_center_address || '',
          delivery_radius: (d.deliveryRadius ?? d.delivery_radius ?? '').toString(),
          delivery_pincodes: Array.isArray(d.deliveryPincodes) ? d.deliveryPincodes.join(', ') : (d.delivery_pincodes || ''),
        });
        
        // Load existing images
        const existingMedia = listing.media || listing.images || [];
        if (existingMedia.length > 0) {
          const loadedImages = existingMedia.map((url, idx) => ({
            id: `existing_${idx}`,
            file: null, // Existing images don't have file object
            preview: typeof url === 'string' ? url : url.url || url.src,
            rawImage: typeof url === 'string' ? url : url.url || url.src,
            isExisting: true,
            url: typeof url === 'string' ? url : url.url || url.src
          }));
          setImages(loadedImages);
        }
        
        // Load availability if present
        if (listing.data?.availability || listing.availability) {
          setAvailability(listing.data?.availability || listing.availability);
        }
        
        // Load advanced data if present
        setAdvancedData(prev => ({
          ...prev,
          // Tax fields (HSN/SAC and rate stay in advanced)
          hsn_code: d.hsnCode || d.hsn_code || listing.hsn_sac_code || '',
          tax_rate: (d.taxRate ?? d.tax_rate ?? '').toString(),
          taxable: d.chargeTax ?? d.taxable ?? listing.taxable ?? false,
          // SEO fields
          meta_title: d.meta_title || listing.seo?.title || '',
          meta_description: d.meta_description || listing.seo?.description || '',
          slug: listing.slug || d.slug || '',
          // Variants
          has_variants: (listing.variants?.length > 0) || d.has_variants || d.hasVariants || false,
          variants: listing.variants || [],
          variantCombinations: listing.variantCombinations || listing.variant_combinations || [],
          // Custom Options
          has_custom_options: d.has_custom_options || d.hasCustomOptions || false,
          customOptionsConfig: d.customOptionsConfig || d.productOptionsConfig || { options: [], hasOptions: false },
          // Product: Serviceable Areas
          serviceable_type: d.serviceableType || d.serviceable_type || 'country',
          serviceable_states: d.serviceableStates || d.serviceable_states || [],
          international_regions: d.internationalRegions || d.international_regions || [],
          // Product: Return & Cancellation Policy
          cancellation_allowed: d.cancellationAllowed ?? d.cancellation_allowed ?? true,
          cancellation_window: d.cancellationWindow || d.cancellation_window || '24hrs',
          cancellation_timing: d.cancellationTiming || d.cancellation_timing || 'before_dispatch',
          return_policy: d.returnPolicy || d.return_policy || 'return_exchange',
          return_exchange_window: (d.returnExchangeWindow || d.return_exchange_window || '7').toString(),
          // Service: Logistics Charges
          has_logistics_charges: d.hasLogisticsCharges ?? d.has_logistics_charges ?? false,
          logistics_charge_type: d.logisticsChargeType || d.logistics_charge_type || 'fixed',
          logistics_base_charge: (d.logisticsBaseCharge ?? d.logistics_base_charge ?? '').toString(),
          logistics_per_km_rate: (d.logisticsPerKmRate ?? d.logistics_per_km_rate ?? '').toString(),
          logistics_free_radius: (d.logisticsFreeRadius ?? d.logistics_free_radius ?? '5').toString(),
          logistics_description: d.logisticsDescription || d.logistics_description || '',
          // Service: Holidays
          holidays: d.holidays || listing.holidays || [],
          // Service: Service Guarantee
          service_guarantee_enabled: d.service_guarantee?.enabled ?? d.service_guarantee_enabled ?? false,
          service_guarantee_type: d.service_guarantee?.type || d.service_guarantee_type || 'full',
          service_guarantee_refund_pct: (d.service_guarantee?.refund_percentage ?? d.service_guarantee_refund_pct ?? '').toString(),
          service_guarantee_days: (d.service_guarantee?.days_limit ?? d.service_guarantee_days ?? '').toString(),
          // Service: Policies & Terms
          svc_cancellation_allowed: d.cancellationAllowed ?? d.svc_cancellation_allowed ?? false,
          svc_cancellation_window: d.cancellationWindow || d.svc_cancellation_window || '24hrs',
          svc_cancellation_timing: d.cancellationTiming || d.svc_cancellation_timing || 'before_service_date',
          svc_terms: d.terms || d.svc_terms || '',
          svc_cancellation_details: d.cancellationPolicyDetails || d.svc_cancellation_details || '',
        }));
        
        // Auto-expand advanced section if it has data
        if (listing.variants?.length > 0 || listing.data?.has_custom_options || listing.data?.hsn_code) {
          setShowAdvanced(true);
        }
      }
    } catch (err) {
      console.error('Failed to load listing:', err);
      toast({
        title: '❌ Error',
        description: 'Failed to load listing data',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingListing(false);
    }
  };

  // Fetch categories when listing type changes
  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await apiCall('/api/categories/');
      const filtered = (response || [])
        .filter(cat => {
          const types = cat.listing_types || [];
          return types.includes(listingType) || types.length === 0;
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCategories(filtered);
      // Only reset category/subcategory when NOT in edit mode
      if (!editMode) {
        setFormData(prev => ({ ...prev, category: '', subcategory: '' }));
        setSubcategories([]);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [apiCall, listingType, editMode]);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, listingType, fetchCategories]);

  // Resolve category by ID, slug, or name
  const resolveCategory = useCallback((categoryValue) => {
    if (!categoryValue || categories.length === 0) return null;
    return categories.find(c => 
      c.id === categoryValue || c.slug === categoryValue || c.name === categoryValue
    );
  }, [categories]);

  // When categories load in edit mode, resolve the stored category to an ID
  useEffect(() => {
    if (editMode && categories.length > 0 && formData.category) {
      const resolved = resolveCategory(formData.category);
      if (resolved && resolved.id !== formData.category) {
        // Category was stored as name, resolve to ID for the dropdown
        editInitialLoadRef.current = true; // Prevent subcategory reset
        setFormData(prev => ({ ...prev, category: resolved.id }));
      }
    }
  }, [editMode, categories, resolveCategory]);

  // When subcategories load in edit mode, resolve the stored subcategory
  useEffect(() => {
    if (editMode && subcategories.length > 0 && formData.subcategory) {
      const resolvedSub = subcategories.find(s => 
        s.id === formData.subcategory || s.slug === formData.subcategory || s.name === formData.subcategory
      );
      if (resolvedSub && resolvedSub.id !== formData.subcategory) {
        setFormData(prev => ({ ...prev, subcategory: resolvedSub.id }));
      }
    }
  }, [editMode, subcategories, formData.subcategory]);

  // iter-331 — Resolve the mandatory license type (if any) for the
  // currently selected category/subcategory. Subcategory override wins.
  const requiredLicenseType = useMemo(() => {
    const selectedCat = resolveCategory(formData.category);
    const selectedSub = subcategories.find(s => s.id === formData.subcategory);
    return selectedSub?.required_license_type || selectedCat?.required_license_type || null;
  }, [formData.category, formData.subcategory, subcategories, resolveCategory]);

  // iter-332 — Resolve the full CMS-driven compliance config (Shelf-Life/
  // Expiry + Warranty/Guarantee + Return Window) for the selected
  // category/subcategory. Every field is admin-configurable — nothing
  // here is hardcoded to a specific taxonomy. Subcategory value wins
  // per-field when explicitly set, else falls back to the category.
  const complianceConfig = useMemo(() => {
    const selectedCat = resolveCategory(formData.category);
    const selectedSub = subcategories.find(s => s.id === formData.subcategory);
    const pick = (field) => (selectedSub?.[field] ?? selectedCat?.[field]);
    return {
      enable_expiry_rules: !!pick('enable_expiry_rules'),
      expiry_date_format: pick('expiry_date_format') || 'shelf_life',
      expiry_legal_label: pick('expiry_legal_label') || 'Best Before',
      min_guaranteed_shelf_life_days: pick('min_guaranteed_shelf_life_days') || null,
      min_guaranteed_shelf_life_type: pick('min_guaranteed_shelf_life_type') || 'days',
      min_guaranteed_shelf_life_percent: pick('min_guaranteed_shelf_life_percent') || null,
      enable_warranty_rules: !!pick('enable_warranty_rules'),
      default_assurance_type: pick('default_assurance_type') || 'manufacturer_warranty',
      default_warranty_duration_unit: pick('default_warranty_duration_unit') || 'months',
      enable_return_window: !!pick('enable_return_window'),
      return_window_days: pick('return_window_days') || null,
      // iter-355 — Admin/Super Admin Buying Mode Override (temporary
      // compliance hold), ported from the desktop ComplianceCard (iter-350)
      // which never got mirrored into this mobile Lite form.
      buying_mode_override_enabled: !!pick('buying_mode_override_enabled'),
      buying_mode_override_type: pick('buying_mode_override_type') || 'enquiry',
    };
  }, [formData.category, formData.subcategory, subcategories, resolveCategory]);

  // iter-355 — Prefill a newly-added license row's number/photo from the
  // vendor's last-used value for that license type (parity with desktop
  // ComplianceCard's applySavedDefault, iter-349).
  const applySavedDefault = useCallback((row) => {
    const saved = defaultLicenses[row.type];
    if (saved && !row.number) {
      return { ...row, number: saved.number || '', image_url: saved.image_url || row.image_url };
    }
    return row;
  }, [defaultLicenses]);

  // iter-346 — Effective freshness-guarantee floor in days for THIS
  // listing: fixed days, or a % of the listing's own declared total
  // shelf life (scales per product). Mirrors backend
  // utils/listing_compliance.py::resolve_shelf_life_floor_days.
  const shelfLifeFloorDays = useMemo(() => {
    if (complianceConfig.min_guaranteed_shelf_life_type === 'percent') {
      const totalDays = parseInt(formData.shelf_life_days, 10);
      if (complianceConfig.min_guaranteed_shelf_life_percent && !isNaN(totalDays)) {
        return Math.round(totalDays * complianceConfig.min_guaranteed_shelf_life_percent / 100);
      }
      return null;
    }
    return complianceConfig.min_guaranteed_shelf_life_days;
  }, [complianceConfig, formData.shelf_life_days]);

  const shelfLifeGuaranteeWording = useMemo(() => {
    if (complianceConfig.min_guaranteed_shelf_life_type === 'percent') {
      return complianceConfig.min_guaranteed_shelf_life_percent
        ? `at least ${complianceConfig.min_guaranteed_shelf_life_percent}% of the Total Shelf Life`
        : null;
    }
    return complianceConfig.min_guaranteed_shelf_life_days ? `at least ${complianceConfig.min_guaranteed_shelf_life_days} days` : null;
  }, [complianceConfig]);

  // Pre-fill the warranty assurance type/unit defaults from the category
  // config the first time warranty rules turn on for this listing.
  useEffect(() => {
    if (!complianceConfig.enable_warranty_rules) return;
    setFormData(prev => ({
      ...prev,
      warranty_assurance_type: prev.warranty_assurance_type || complianceConfig.default_assurance_type,
      warranty_duration_unit: prev.warranty_duration_unit || complianceConfig.default_warranty_duration_unit,
    }));
  }, [complianceConfig.enable_warranty_rules]);

  const LICENSE_TYPE_LABELS = {
    FSSAI: 'FSSAI License', BIS: 'BIS / ISI Registration',
    CDSCO: 'CDSCO / State Drug License', AGMARK: 'AGMARK Certification', ISI: 'ISI Mark Registration',
  };

  // Auto-add a row pre-set to the required type so the vendor just has to
  // type the number, without blocking manual removal/editing. iter-355:
  // now pre-fills the vendor's last-used number/photo for that type too.
  useEffect(() => {
    if (!requiredLicenseType) return;
    const already = (formData.licenses_certifications || []).some(l => l.type === requiredLicenseType);
    if (!already) {
      const newRow = applySavedDefault({ type: requiredLicenseType, number: '' });
      setFormData(prev => ({
        ...prev,
        licenses_certifications: [...(prev.licenses_certifications || []), newRow],
      }));
    }
  }, [requiredLicenseType, defaultLicenses]); // eslint-disable-line


  useEffect(() => {
    if (formData.category) {
      const selectedCat = resolveCategory(formData.category);
      if (selectedCat?.subcategories) {
        const sortedSubs = [...selectedCat.subcategories]
          .filter(sub => sub.is_active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSubcategories(sortedSubs);
      } else if (selectedCat) {
        // Category resolved but has no subcategories
        setSubcategories([]);
      }
      // Only consume ref / reset subcategory when category actually resolved
      // (prevents premature ref consumption when categories haven't loaded yet)
      if (selectedCat) {
        if (editInitialLoadRef.current) {
          editInitialLoadRef.current = false;
        } else {
          setFormData(prev => ({ ...prev, subcategory: '' }));
        }
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories, resolveCategory]);

  // Clear specific field validation error when user edits that field
  // NOTE: This useCallback MUST be before the early return to follow Rules of Hooks
  const clearFieldError = useCallback((field) => {
    setValidationErrors(prev => prev.filter(e => e.field !== field));
  }, []);

  // Certificate upload state — MUST be declared before the early return below
  // to comply with Rules of Hooks (was previously at ~line 1185, causing a
  // "Rendered more hooks than during the previous render" error the first
  // time the overlay opened). Test-agent hoisted to unblock iter-355 QA.
  const [certUploadingIdx, setCertUploadingIdx] = useState(null);

  if (!isOpen) return null;

  // ============ IMAGE HANDLERS (Multi-image support) ============
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Limit total images to 10
    const remainingSlots = 10 - images.length;
    if (remainingSlots <= 0) {
      toast({
        title: '📷 Maximum images reached',
        description: 'You can upload up to 10 images',
        variant: 'destructive',
        duration: 3000
      });
      return;
    }
    
    const filesToProcess = files.slice(0, remainingSlots);
    
    filesToProcess.forEach((file, idx) => {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: '📷 Image too large',
          description: `${file.name} is over 10MB and was skipped`,
          variant: 'destructive',
          duration: 3000
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageId = `img_${Date.now()}_${idx}`;
        const newImage = {
          id: imageId,
          file: file,
          preview: reader.result,
          rawImage: reader.result,
          uploadStatus: 'pending', // Track upload status
          uploadedUrl: null
        };
        setImages(prev => [...prev, newImage]);
        clearFieldError('image');
        
        // Start background upload immediately
        setTimeout(() => {
          uploadImageInBackground(imageId, file);
        }, 100);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input value to allow selecting same file again
    e.target.value = '';
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const openCropperForImage = (imageId) => {
    const img = images.find(i => i.id === imageId);
    if (img?.rawImage) {
      setCropperImage(img.rawImage);
      setCropperImageIndex(imageId);
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedFile, croppedPreview) => {
    if (cropperImageIndex) {
      // Update the image with new cropped file and reset upload status
      setImages(prev => prev.map(img => 
        img.id === cropperImageIndex 
          ? { ...img, file: croppedFile, preview: croppedPreview, uploadStatus: 'pending', uploadedUrl: null }
          : img
      ));
      
      // Re-upload the cropped image in background
      setTimeout(() => {
        uploadImageInBackground(cropperImageIndex, croppedFile);
      }, 100);
    }
    setShowCropper(false);
    setCropperImage(null);
    setCropperImageIndex(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
    setCropperImageIndex(null);
  };

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        const maxSize = 1200;
        let { width, height } = img;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          } else {
            reject(new Error('Image compression failed'));
          }
        }, 'image/jpeg', 0.8);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image for compression'));
      };
      img.src = objectUrl;
    });
  };

  // ============ BACKGROUND IMAGE UPLOAD ============

  // iter-344 — Vendor certificate/license photo upload (per license row)
  const uploadLicenseCertImage = async (idx, file) => {
    setCertUploadingIdx(idx);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData,
      });
      if (response.ok) {
        const data = await response.json();
        const uploadedUrl = data.cdn_url || data.file_url || data.url;
        setFormData(prev => {
          const next = [...prev.licenses_certifications];
          next[idx] = { ...next[idx], image_url: uploadedUrl };
          return { ...prev, licenses_certifications: next };
        });
      } else {
        toast({ title: 'Upload failed', description: 'Could not upload the certificate photo. Try again.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Upload failed', description: 'Could not upload the certificate photo. Try again.', variant: 'destructive' });
    } finally {
      setCertUploadingIdx(null);
    }
  };

  const uploadImageInBackground = async (imageId, file) => {
    // Update status to uploading
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, uploadStatus: 'uploading' } : img
    ));

    try {
      // Compress the image first
      const compressedImage = await compressImage(file);
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', compressedImage);
      
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      if (response.ok) {
        const data = await response.json();
        const uploadedUrl = data.cdn_url || data.file_url || data.url;
        
        // Update image with uploaded URL and completed status
        setImages(prev => prev.map(img => 
          img.id === imageId 
            ? { ...img, uploadStatus: 'completed', uploadedUrl } 
            : img
        ));
        
        return uploadedUrl;
      } else {
        // Parse error detail from backend
        let errorMsg = `Upload failed (${response.status})`;
        try {
          const errData = await response.json();
          errorMsg = errData.detail || errorMsg;
        } catch { /* ignore parse errors */ }
        
        // Show user-facing toast for specific errors
        if (response.status === 403) {
          toast({
            title: 'Upload Blocked',
            description: errorMsg.includes('active') 
              ? 'Your account is not yet active. Please contact admin for approval before uploading images.'
              : errorMsg,
            variant: 'destructive',
            duration: 6000
          });
        } else if (response.status === 413) {
          toast({
            title: 'Storage Full',
            description: errorMsg,
            variant: 'destructive',
            duration: 5000
          });
        }
        
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error(`[LiteMode] Image ${imageId} upload failed:`, error);
      
      // Update status to failed with error message
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, uploadStatus: 'failed', uploadError: error.message } : img
      ));
      
      return null;
    }
  };

  // Retry failed upload
  const retryImageUpload = (imageId) => {
    const img = images.find(i => i.id === imageId);
    if (img?.file && img.uploadStatus === 'failed') {
      uploadImageInBackground(imageId, img.file);
    }
  };

  // ============ VALIDATION ============
  const validateForm = () => {
    const errors = [];
    
    // Title: Min 7 chars
    if (!formData.title.trim() || formData.title.trim().length < 7) {
      errors.push({ field: 'title', message: 'Title must be at least 7 characters' });
    }
    
    // Description: Min 15 chars
    if (!formData.description.trim() || formData.description.trim().length < 15) {
      errors.push({ field: 'description', message: 'Description must be at least 15 characters' });
    }
    
    // Category required
    if (!formData.category) {
      errors.push({ field: 'category', message: 'Please select a category' });
    }
    
    // Image required for publishing - EXCEPT for Professional Services (service listings)
    // Professional Services can use default subcategory icons
    const isProfessionalService = listingType === 'service' && isProfessionalServicesCategory(formData.categoryName);
    if (images.length === 0 && !isProfessionalService) {
      errors.push({ field: 'image', message: 'At least 1 photo is required' });
    }
    
    // Price validation (events can be free)
    const priceValue = parseFloat(formData.price) || 0;
    if (listingType !== 'event' && priceValue <= 0) {
      errors.push({ field: 'price', message: 'Please enter a valid price' });
    }

    // Event date validation
    if (listingType === 'event' && !formData.event_date) {
      errors.push({ field: 'event_date', message: 'Please select an event date' });
    }

    // Service/Rental availability validation
    if (typeConfig.showAvailability && !(listingType === 'service' && formData.bookingMode === 'enquiry')) {
      const enabledDays = (availability.business_hours || []).filter(bh => bh.enabled);
      if (enabledDays.length === 0) {
        errors.push({ field: 'availability', message: 'Please select available days' });
      }
    }

    // Rental location validation
    if (typeConfig.showRentalLocation) {
      if (!formData.rental_address.trim()) {
        errors.push({ field: 'rental_address', message: 'Pickup address is required' });
      }
      if (!formData.rental_city.trim()) {
        errors.push({ field: 'rental_city', message: 'City is required' });
      }
    }

    // Service location validation (only if physical)
    if (typeConfig.showLocation && formData.service_location === 'provider_location') {
      if (!formData.service_city.trim()) {
        errors.push({ field: 'service_city', message: 'City is required for physical services' });
      }
    }

    // iter-229i — Weight is required when the listing ships. Backend
    // model_validator rejects requires_shipping=true with empty weight,
    // but we surface the friendly UI error first so vendors don't see
    // a confusing 422 server response.
    if (typeConfig.showShipping && formData.requires_shipping) {
      const w = parseFloat(formData.weight) || 0;
      if (w <= 0) {
        errors.push({
          field: 'weight',
          message: 'Weight is required for products that ship. Buyers see the wrong shipping price otherwise.',
        });
      }
    }

    // iter-364 — Country of Origin (and license) only required when the
    // admin has activated compliance for this category/subcategory in the
    // CMS. Mirrors backend utils/listing_compliance.py — used to be
    // unconditional here, blocking every Service/Event/Rental Lite-mode
    // listing under a category with no compliance flags set.
    const complianceActivatedLite = !!(requiredLicenseType || complianceConfig.enable_expiry_rules || complianceConfig.enable_warranty_rules);
    if (complianceActivatedLite && (!formData.country_of_origin || !formData.country_of_origin.trim())) {
      errors.push({ field: 'country_of_origin', message: 'Country of Origin is required' });
    }

    // iter-331 — Category-mandated license (FSSAI/BIS/CDSCO/etc.)
    if (requiredLicenseType) {
      const matched = (formData.licenses_certifications || []).some(
        l => l.type === requiredLicenseType && (l.number || '').trim()
      );
      if (!matched) {
        errors.push({
          field: 'licenses_certifications',
          message: `${LICENSE_TYPE_LABELS[requiredLicenseType] || requiredLicenseType} number is required for this category`,
        });
      }
    }

    // iter-332 — Category-mandated Shelf-Life/Expiry info
    if (complianceConfig.enable_expiry_rules) {
      if (complianceConfig.expiry_date_format === 'exact_date') {
        if (!formData.expiry_date) {
          errors.push({ field: 'expiry_date', message: `${complianceConfig.expiry_legal_label} date is required for this category` });
        }
      } else {
        const days = parseInt(formData.shelf_life_days, 10);
        if (!formData.shelf_life_days || isNaN(days)) {
          errors.push({ field: 'shelf_life_days', message: `${complianceConfig.expiry_legal_label} (shelf life in days) is required for this category` });
        } else if (complianceConfig.min_guaranteed_shelf_life_type !== 'percent' && complianceConfig.min_guaranteed_shelf_life_days && days < complianceConfig.min_guaranteed_shelf_life_days) {
          errors.push({ field: 'shelf_life_days', message: `Minimum guaranteed shelf life for this category is ${complianceConfig.min_guaranteed_shelf_life_days} days` });
        }
      }
      if (formData.freshness_guarantee_days && shelfLifeFloorDays) {
        const freshness = parseInt(formData.freshness_guarantee_days, 10);
        if (!isNaN(freshness) && freshness < shelfLifeFloorDays) {
          errors.push({ field: 'freshness_guarantee_days', message: `Freshness guarantee cannot be below the platform minimum of ${shelfLifeFloorDays} days` });
        }
      }
    }

    // iter-332 — Category-mandated Warranty/Guarantee info
    if (complianceConfig.enable_warranty_rules) {
      const durationValue = parseInt(formData.warranty_duration_value, 10);
      if (!formData.warranty_duration_value || isNaN(durationValue) || durationValue <= 0) {
        errors.push({ field: 'warranty_duration_value', message: 'Warranty/Guarantee duration is required for this category' });
      }
    }

    setValidationErrors(errors);
    return errors;
  };

  // ============ FORM SUBMISSION ============
  const handleSubmit = async () => {
    setError(null);
    
    // Validate form - use fresh errors directly (not stale state)
    const errors = validateForm();
    if (errors.length > 0) {
      toast({ 
        title: '⚠️ Missing Required Fields', 
        description: errors[0]?.message || 'Please fill all required fields',
        variant: 'destructive',
        duration: 4000
      });
      return;
    }

    setLoading(true);
    setUploadProgress(10);
    
    try {
      // Generate smart defaults
      const autoSku = formData.title.toUpperCase().substring(0, 3) + '-' + Date.now().toString().substring(8);
      const slug = formData.title.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/g, '');
      const selectedCategory = resolveCategory(formData.category);
      const priceValue = parseFloat(formData.price) || 0;
      const weightValue = parseFloat(formData.weight) || 0;

      setUploadProgress(20);

      // Build the data dict (matches regular mode format)
      const dataDict = {
        price: priceValue,
        comparePrice: null,
        sku: autoSku,
        inventory: typeConfig.showStock ? (parseInt(formData.stock) || 1) : null,
        trackInventory: typeConfig.showStock,
        productType: formData.product_type || 'physical',
        requiresShipping: typeConfig.showShipping ? formData.requires_shipping : false,
        weight: weightValue || 0.5,
        weightUnit: formData.weight_unit || 'kg',
        chargeTax: formData.charge_tax,
        // Store category/subcategory IDs for edit-mode resolution
        categoryId: formData.category,
        subcategoryId: formData.subcategory || null,
        // Tax details (from advanced section)
        hsnCode: advancedData.hsn_code || null,
        taxRate: advancedData.tax_rate ? parseFloat(advancedData.tax_rate) : null,
        // Digital delivery
        ...(formData.product_type === 'digital' && {
          digitalDownloadUrl: formData.digital_download_url || null,
        }),
        // Product: Serviceable Areas & Policies
        ...(listingType === 'product' && {
          // iter-355 — Buying Mode (Cart vs Enquiry), was never sent for
          // physical products even though the UI field now exists.
          // Matches desktop UniversalSinglePageForm's convention.
          bookingMode: formData.bookingMode,
          booking_mode: formData.bookingMode || null,
          serviceableType: advancedData.serviceable_type,
          serviceableStates: advancedData.serviceable_states,
          internationalRegions: advancedData.international_regions,
          cancellationAllowed: advancedData.cancellation_allowed,
          cancellationWindow: advancedData.cancellation_window,
          cancellationTiming: advancedData.cancellation_timing,
          returnPolicy: advancedData.return_policy,
          returnExchangeWindow: advancedData.return_exchange_window,
        }),
        // Event-specific
        ...(listingType === 'event' && {
          event_date: formData.event_date,
          event_time: formData.event_time || '10:00',
          registration_type: priceValue > 0 ? 'paid' : 'free',
          organizerName: formData.organizer_name || null,
          organizer_name: formData.organizer_name || null,
          isRecurring: formData.is_recurring,
          recurrenceFrequency: formData.is_recurring ? formData.recurrence_frequency : null,
          inclusions: formData.event_inclusions || [],
          exclusions: formData.event_exclusions || [],
          cancellationAllowed: advancedData.svc_cancellation_allowed,
          cancellationWindow: advancedData.svc_cancellation_window,
          terms: advancedData.svc_terms || null,
        }),
        // Rental-specific
        ...(listingType === 'rental' && (() => {
          // Compute all 3 rate variants from the primary period so every reader
          // (listing card, RentalDetails, Rentals page, Shopify sync, Shopify
          // metafields) finds a sensible price regardless of which key it
          // looks for. Snake_case backward-compat keys are also written.
          const period = formData.rental_period || 'daily';
          const dailyRate =
            period === 'daily' ? priceValue
            : period === 'weekly' ? priceValue / 7
            : period === 'monthly' ? priceValue / 30
            : priceValue;
          const weeklyRate =
            period === 'weekly' ? priceValue
            : period === 'daily' ? priceValue * 7
            : period === 'monthly' ? priceValue / 4
            : priceValue * 7;
          const monthlyRate =
            period === 'monthly' ? priceValue
            : period === 'daily' ? priceValue * 30
            : period === 'weekly' ? priceValue * 4
            : priceValue * 30;
          return {
            rental_period: period,
            pricingUnit: period,                  // 'daily' | 'weekly' | 'monthly'
            // Primary (selected) period rate — what the seller actually entered
            [`${period}_rate`]: priceValue,        // legacy snake_case key
            // CamelCase variants — what readers expect
            dailyRate: Math.round(dailyRate * 100) / 100,
            weeklyRate: Math.round(weeklyRate * 100) / 100,
            monthlyRate: Math.round(monthlyRate * 100) / 100,
            rentalType: period,                    // metafield key
            // Other rental fields
            security_deposit: parseFloat(formData.security_deposit) || 0,
            securityDeposit: parseFloat(formData.security_deposit) || 0,
            min_rental_period: parseInt(formData.min_rental_period) || 1,
            minRentalPeriod: parseInt(formData.min_rental_period) || 1,
            min_rental_unit: formData.min_rental_unit,
            rental_address: formData.rental_address,
            rental_city: formData.rental_city,
            rental_pincode: formData.rental_pincode,
            availabilitySettings: availability,
            serviceableArea: formData.serviceableArea,
            bookingMode: formData.rental_booking_mode,
            rentalTemplate: formData.rental_template,
            ...(formData.rental_template === 'venue_booking' && {
              nearestLandmark: formData.venue_nearest_landmark || null,
              nearestTransport: formData.venue_nearest_transport || null,
              parkingAvailable: formData.venue_parking_available,
              googleMapsLink: formData.venue_google_maps_link || null,
            }),
            travelBooking: {
              allowTravelBooking: formData.allow_travel_booking,
              travelChargePerKm: formData.allow_travel_booking ? parseFloat(formData.travel_charge_per_km) || 0 : 0,
              minimumTravelFee: formData.allow_travel_booking ? parseFloat(formData.min_travel_fee) || 0 : 0,
              maxTravelDistanceKm: formData.allow_travel_booking ? parseFloat(formData.max_travel_distance_km) || 0 : 0,
            },
            damagePolicy: advancedData.svc_cancellation_details || null,
            termsConditions: advancedData.svc_terms || null,
            cancellationAllowed: advancedData.svc_cancellation_allowed,
            cancellationWindow: advancedData.svc_cancellation_window,
          };
        })()),
        // Service-specific
        ...(listingType === 'service' && {
          booking_type: formData.bookingMode === 'enquiry' ? 'enquiry' : 'booking',
          bookingMode: formData.bookingMode,
          locationType: formData.service_location,
          duration: parseInt(formData.service_duration) || 60,
          service_location: formData.service_location,
          service_duration: parseInt(formData.service_duration) || 60,
          meetingLink: formData.meeting_link || null,
          serviceAddress: formData.service_address || null,
          travelRadius: formData.travel_radius ? parseFloat(formData.travel_radius) : null,
          service_city: formData.service_city || null,
          inclusions: formData.inclusions || [],
          exclusions: formData.exclusions || [],
          availabilitySettings: availability,
          serviceableArea: formData.serviceableArea,
          travelBooking: {
            allowTravelBooking: formData.allow_travel_booking,
            travelChargePerKm: formData.allow_travel_booking ? parseFloat(formData.travel_charge_per_km) || 0 : 0,
            minimumTravelFee: formData.allow_travel_booking ? parseFloat(formData.min_travel_fee) || 0 : 0,
            maxTravelDistanceKm: formData.allow_travel_booking ? parseFloat(formData.max_travel_distance_km) || 0 : 0,
          },
          // Logistics charges
          hasLogisticsCharges: advancedData.has_logistics_charges,
          ...(advancedData.has_logistics_charges && {
            logisticsChargeType: advancedData.logistics_charge_type,
            logisticsBaseCharge: parseFloat(advancedData.logistics_base_charge) || 0,
            logisticsPerKmRate: parseFloat(advancedData.logistics_per_km_rate) || 0,
            logisticsFreeRadius: parseFloat(advancedData.logistics_free_radius) || 5,
            logisticsDescription: advancedData.logistics_description,
          }),
          // Holidays
          holidays: advancedData.holidays,
          // Service Guarantee
          service_guarantee: {
            enabled: advancedData.service_guarantee_enabled,
            type: advancedData.service_guarantee_type,
            refund_percentage: advancedData.service_guarantee_refund_pct ? parseInt(advancedData.service_guarantee_refund_pct) : null,
            days_limit: advancedData.service_guarantee_days ? parseInt(advancedData.service_guarantee_days) : null,
          },
          // Policies & Terms
          cancellationAllowed: advancedData.svc_cancellation_allowed,
          cancellationWindow: advancedData.svc_cancellation_window,
          cancellationTiming: advancedData.svc_cancellation_timing,
          terms: advancedData.svc_terms,
          cancellationPolicyDetails: advancedData.svc_cancellation_details,
        }),
        // Subscription-specific
        ...(listingType === 'subscription' && {
          // Multi-key write so every reader (Shopify sync validation,
          // /api/listings filters, listing card preview) finds the price
          // regardless of which key it's hard-coded against.
          monthly_price: priceValue,
          monthlyPrice: priceValue,
          unit_price: priceValue,
          basePlanPrice: priceValue,
          base_plan_price: priceValue,
          billingInterval: 'monthly',
          auto_renewal: true,
          deliveryType: formData.delivery_type,
          deliveryCenterAddress: formData.delivery_center_address || null,
          deliveryRadius: formData.delivery_radius ? parseFloat(formData.delivery_radius) : null,
          deliveryPincodes: formData.delivery_type === 'pincodes'
            ? formData.delivery_pincodes.split(',').map(p => p.trim()).filter(Boolean)
            : [],
          cancellationAllowed: advancedData.svc_cancellation_allowed,
          cancellationWindow: advancedData.svc_cancellation_window,
          terms: advancedData.svc_terms || null,
        }),
        // Custom Options
        ...(advancedData.has_custom_options && {
          hasCustomOptions: true,
          customOptionsConfig: advancedData.customOptionsConfig,
          productOptionsConfig: advancedData.customOptionsConfig
        }),
        // Metadata
        metadata: {
          is_lite_mode_created: !editMode,
          is_lite_mode_edited: editMode,
          created_via: 'lite_dashboard',
          lite_version: '4.0',
          has_advanced_details: showAdvanced
        }
      };

      // ============ AUTO-REPLICATE: Save vendor defaults ============
      try {
        // Always update universal cross-type policy key
        const universalPolicies = {
          cancellation_allowed: listingType === 'product' ? advancedData.cancellation_allowed : advancedData.svc_cancellation_allowed,
          cancellation_window: listingType === 'product' ? advancedData.cancellation_window : advancedData.svc_cancellation_window,
          terms: advancedData.svc_terms,
          cancellation_details: advancedData.svc_cancellation_details,
        };
        localStorage.setItem('vendor_universal_policies', JSON.stringify(universalPolicies));

        if (listingType === 'product') {
          localStorage.setItem('vendor_product_defaults', JSON.stringify({
            serviceable_type: advancedData.serviceable_type,
            serviceable_states: advancedData.serviceable_states,
            international_regions: advancedData.international_regions,
            cancellation_allowed: advancedData.cancellation_allowed,
            cancellation_window: advancedData.cancellation_window,
            cancellation_timing: advancedData.cancellation_timing,
            return_policy: advancedData.return_policy,
            return_exchange_window: advancedData.return_exchange_window,
          }));
        } else if (listingType === 'service') {
          localStorage.setItem('vendor_service_defaults', JSON.stringify({
            service_guarantee_enabled: advancedData.service_guarantee_enabled,
            service_guarantee_type: advancedData.service_guarantee_type,
            service_guarantee_refund_pct: advancedData.service_guarantee_refund_pct,
            service_guarantee_days: advancedData.service_guarantee_days,
            svc_cancellation_allowed: advancedData.svc_cancellation_allowed,
            svc_cancellation_window: advancedData.svc_cancellation_window,
            svc_cancellation_timing: advancedData.svc_cancellation_timing,
            svc_terms: advancedData.svc_terms,
            svc_cancellation_details: advancedData.svc_cancellation_details,
            serviceableArea: formData.serviceableArea,
          }));
        } else if (listingType === 'rental') {
          localStorage.setItem('vendor_rental_defaults', JSON.stringify({
            cancellation_allowed: advancedData.svc_cancellation_allowed,
            cancellation_window: advancedData.svc_cancellation_window,
            terms: advancedData.svc_terms,
            damage_policy: advancedData.svc_cancellation_details,
          }));
        } else if (listingType === 'event') {
          localStorage.setItem('vendor_event_defaults', JSON.stringify({
            cancellation_allowed: advancedData.svc_cancellation_allowed,
            cancellation_window: advancedData.svc_cancellation_window,
            terms: advancedData.svc_terms,
          }));
        } else if (listingType === 'subscription') {
          localStorage.setItem('vendor_subscription_defaults', JSON.stringify({
            cancellation_allowed: advancedData.svc_cancellation_allowed,
            cancellation_window: advancedData.svc_cancellation_window,
            terms: advancedData.svc_terms,
          }));
        }
      } catch (e) { /* ignore storage errors */ }

      // Resolve subcategory name from ID for top-level field (consistent with category)
      const selectedSubcategory = subcategories.find(s => s.id === formData.subcategory);

      // Build listing payload (top-level fields match Pydantic model)
      const listingPayload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        listing_type: listingType,
        category: selectedCategory?.name || formData.category,
        subcategory: selectedSubcategory?.name || formData.subcategory || null,
        hsn_sac_code: advancedData.hsn_code || null,
        product_type: formData.product_type || 'physical',
        // iter-331 — Legal disclosure fields
        country_of_origin: formData.country_of_origin?.trim() || null,
        licenses_certifications: (formData.licenses_certifications || []).filter(l => (l.number || '').trim()),
        excluded_license_types: formData.excluded_license_types || [],
        // iter-332 — Shelf-Life/Expiry + Warranty/Guarantee disclosures
        shelf_life: complianceConfig.enable_expiry_rules ? {
          format: complianceConfig.expiry_date_format,
          shelf_life_days: complianceConfig.expiry_date_format === 'exact_date' ? null : (parseInt(formData.shelf_life_days, 10) || null),
          expiry_date: complianceConfig.expiry_date_format === 'exact_date' ? (formData.expiry_date || null) : null,
          min_freshness_guarantee_days: parseInt(formData.freshness_guarantee_days, 10) || null,
        } : null,
        warranty: complianceConfig.enable_warranty_rules ? {
          assurance_type: formData.warranty_assurance_type,
          duration_value: parseInt(formData.warranty_duration_value, 10) || null,
          duration_unit: formData.warranty_duration_unit,
        } : null,
        // Data dict with all form values (regular mode compatible)
        data: dataDict,
        // Logistics config (Pydantic model field)
        // iter-229i — Send the raw weight (NOT a 0.5kg fallback). Backend
        // model_validator rejects requires_shipping=true with no weight,
        // which is exactly what we want: vendor must fill weight in the
        // form before submit succeeds. Silent 0.5 default was leading to
        // wrong shipping rates in the buyer-facing modal.
        logistics: {
          requires_shipping: typeConfig.showShipping ? formData.requires_shipping : false,
          available_for_pickup: false,
          weight_kg: formData.requires_shipping ? (weightValue || null) : null,
        },
        // Digital delivery config
        ...(formData.product_type === 'digital' && {
          digital_delivery: {
            download_url: formData.digital_download_url || null,
            delivery_methods: ['email', 'storefront'],
          }
        }),
        // Type-specific defaults from config
        ...typeConfig.defaults,
        // Booking type for services
        ...(listingType === 'service' && {
          booking_type: formData.bookingMode === 'enquiry' ? 'enquiry' : 'booking',
          availability: availability.business_hours?.length > 0 ? availability : undefined,
          inclusions: formData.inclusions || [],
          exclusions: formData.exclusions || [],
          holidays: advancedData.holidays || [],
          service_guarantee: advancedData.service_guarantee_enabled ? {
            enabled: true,
            type: advancedData.service_guarantee_type,
            refund_percentage: advancedData.service_guarantee_refund_pct ? parseInt(advancedData.service_guarantee_refund_pct) : null,
            days_limit: advancedData.service_guarantee_days ? parseInt(advancedData.service_guarantee_days) : null,
          } : { enabled: false },
        }),
        // Variants
        ...(advancedData.has_variants && advancedData.variants?.length > 0 && {
          variants: advancedData.variants,
          variant_combinations: advancedData.variantCombinations
        }),
        // Custom Options flag
        ...(advancedData.has_custom_options && {
          has_custom_options: true,
        }),
      };

      setUploadProgress(40);

      let response;
      
      if (editMode && listingId) {
        // UPDATE existing listing
        response = await apiCall(`/api/listings/${listingId}`, {
          method: 'PUT',
          body: JSON.stringify(listingPayload)
        });
        response = { ...response, id: listingId };
      } else {
        // CREATE new listing (backend always sets status to 'draft' for vendors)
        response = await apiCall('/api/listings/', {
          method: 'POST',
          body: JSON.stringify(listingPayload)
        });
      }

      setUploadProgress(55);

      // Handle images - Use already-uploaded URLs from background upload
      const targetListingId = response?.id || listingId;
      const isProfessionalService = listingType === 'service' && isProfessionalServicesCategory(formData.categoryName);
      
      if (targetListingId && (images.length > 0 || isProfessionalService)) {
        try {
          const finalUrls = [];
          const existingUrls = images.filter(img => img.isExisting).map(img => img.url);
          const newImages = images.filter(img => !img.isExisting);
          
          // Add existing URLs first (maintains order - first image is primary)
          finalUrls.push(...existingUrls);
          
          // If no images and Professional Services, generate default icon
          if (images.length === 0 && isProfessionalService) {
            
            // Generate a professional-looking placeholder icon URL
            // We'll use a CDN placeholder service with the subcategory icon
            const subcategorySlug = (formData.subcategoryName || 'Others').toLowerCase().replace(/[^a-z0-9]/g, '-');
            const IconComponent = PROFESSIONAL_SERVICES_ICONS[formData.subcategoryName] || HelpCircle;
            
            // Create a canvas-based icon and upload it
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');
            
            // Draw gradient background
            const gradient = ctx.createLinearGradient(0, 0, 400, 400);
            gradient.addColorStop(0, '#f8fafc');
            gradient.addColorStop(1, '#e2e8f0');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 400);
            
            // Draw decorative circles
            ctx.fillStyle = 'rgba(99, 102, 241, 0.1)';
            ctx.beginPath();
            ctx.arc(200, 200, 140, 0, Math.PI * 2);
            ctx.fill();
            
            ctx.fillStyle = 'rgba(99, 102, 241, 0.15)';
            ctx.beginPath();
            ctx.arc(200, 200, 100, 0, Math.PI * 2);
            ctx.fill();
            
            // Draw icon placeholder circle
            ctx.fillStyle = '#6366f1';
            ctx.beginPath();
            ctx.arc(200, 200, 60, 0, Math.PI * 2);
            ctx.fill();
            
            // Add text (first letter of subcategory)
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const firstLetter = (formData.subcategoryName || 'P').charAt(0).toUpperCase();
            ctx.fillText(firstLetter, 200, 200);
            
            // Add subcategory name below
            ctx.fillStyle = '#475569';
            ctx.font = '500 18px system-ui, -apple-system, sans-serif';
            ctx.fillText(formData.subcategoryName || 'Professional', 200, 320);
            
            // Convert to blob and upload
            const iconBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.9));
            const iconFile = new File([iconBlob], `${subcategorySlug}-icon.png`, { type: 'image/png' });
            
            const iconFormData = new FormData();
            iconFormData.append('file', iconFile);
            
            const token = sessionStorage.getItem('token');
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            
            try {
              const iconResponse = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: iconFormData
              });
              
              if (iconResponse.ok) {
                const iconData = await iconResponse.json();
                const iconUrl = iconData.cdn_url || iconData.file_url || iconData.url;
                if (iconUrl) finalUrls.push(iconUrl);
              }
            } catch (iconErr) {
              console.warn('[LiteMode] Failed to upload default icon:', iconErr);
            }
          } else if (newImages.length > 0) {
            // Check if any new images are still uploading
            const pendingUploads = newImages.filter(img => 
              img.uploadStatus === 'pending' || img.uploadStatus === 'uploading'
            );
          
            if (pendingUploads.length > 0) {
              // Wait for pending uploads with a timeout (max 30 seconds)
              setUploadProgress(65);
              toast({
                title: '⏳ Finalizing images...',
                description: `Waiting for ${pendingUploads.length} image(s) to finish uploading`,
                duration: 3000
              });
              
              const maxWait = 30000; // 30 seconds max
              const startTime = Date.now();
              
              while (Date.now() - startTime < maxWait) {
                // Re-check the current state of images
                const currentImages = await new Promise(resolve => {
                  setImages(prev => {
                    resolve(prev);
                    return prev;
                  });
                });
                
                const stillPending = currentImages.filter(img => 
                  !img.isExisting && (img.uploadStatus === 'pending' || img.uploadStatus === 'uploading')
                );
                
                if (stillPending.length === 0) break;
                
                // Update progress
                const completedCount = newImages.length - stillPending.length;
                setUploadProgress(65 + (25 * completedCount / newImages.length));
                
                await new Promise(r => setTimeout(r, 500)); // Check every 500ms
              }
            }
            
            setUploadProgress(85);
            
            // Collect all uploaded URLs from new images
            // Need to get the latest state
            const latestImages = await new Promise(resolve => {
              setImages(prev => {
                resolve(prev);
                return prev;
              });
            });
            
            for (const img of latestImages.filter(i => !i.isExisting)) {
              if (img.uploadedUrl) {
                finalUrls.push(img.uploadedUrl);
              } else if (img.uploadStatus === 'failed') {
                // Try one more upload for failed images
                const url = await uploadImageInBackground(img.id, img.file);
                if (url) finalUrls.push(url);
              }
            }
          }
          
          // Update listing with all image URLs
          if (finalUrls.length > 0) {
            await apiCall(`/api/listings/${targetListingId}`, {
              method: 'PUT',
              body: JSON.stringify({
                media: finalUrls
              })
            });
          }
          
          setUploadProgress(95);
        } catch (imgErr) {
          console.warn('Image handling failed:', imgErr);
          toast({
            title: '📷 Image Upload Issue',
            description: editMode ? 'Listing updated but some images may be missing.' : 'Listing created but some images may be missing.',
            variant: 'default',
            duration: 5000
          });
        }
      }

      setUploadProgress(96);

      // SUBMIT FOR APPROVAL: After creating/updating, call the submit endpoint
      // Backend forces status='draft' on create, so we need a separate call
      const targetId = targetListingId || response?.id || listingId;
      if (targetId) {
        try {
          const submitResponse = await apiCall(`/api/listings/${targetId}/submit`, {
            method: 'POST'
          });
          
          if (submitResponse?.success === false) {
            // Handle specific submission blocks (account pending, tier limits, etc.)
            const blockReason = submitResponse.message || 'Could not submit for approval';
            console.warn('[LiteMode] Submit blocked:', blockReason);
            toast({
              title: 'Saved as Draft',
              description: blockReason,
              variant: 'default',
              duration: 6000
            });
          } else {
          }
        } catch (submitErr) {
          console.warn('[LiteMode] Submit for approval failed, saved as draft:', submitErr);
          // Don't show error - listing is saved as draft, user can submit later
        }
      }

      setUploadProgress(100);

      // Success message
      if (editMode) {
        toast({
          title: 'Changes Saved!',
          description: `"${formData.title}" has been updated and submitted for review.`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Submitted for Review!',
          description: `"${formData.title}" has been submitted. Once approved by admin, it will be visible to customers.`,
          duration: 6000
        });
      }

      // Callback for parent refresh
      if (onSuccess) onSuccess(response);
      
      if (editMode) {
        // Navigate back to listings
        navigate('/listings');
      } else {
        // Reset form for new listing
        setFormData({
          title: '',
          description: '',
          price: '',
          stock: '1',
          category: '',
          subcategory: '',
          requires_shipping: true,
          weight: '',
          weight_unit: 'kg',
          charge_tax: false,
          product_type: 'physical',
          digital_download_url: '',
          event_date: '',
          event_time: '',
          rental_period: 'daily',
          security_deposit: '0',
          rental_address: '',
          rental_city: '',
          rental_pincode: '',
          min_rental_period: '1',
          min_rental_unit: 'days',
          booking_type: 'enquiry',
          service_location: 'provider_location',
          service_duration: '60',
          service_address: '',
          service_city: '',
          meeting_link: '',
          travel_radius: '',
          inclusions: [],
          exclusions: [],
        });
        setImages([]);
        setValidationErrors([]);
        onClose();
      }
    } catch (error) {
      console.error('Submit error:', error);
      const errorMsg = error.message || 'Something went wrong';
      setError(errorMsg);
      toast({
        title: editMode ? '❌ Update Failed' : '❌ Submission Failed',
        description: `Could not ${editMode ? 'update' : 'submit'} your listing. ${errorMsg}. Please try again.`,
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Helper to check if field has error
  const hasError = (field) => validationErrors.some(e => e.field === field);

  // ============ RENDER ============
  // When isLiteMode, render as a regular div (already inside LiteLayout)
  // Otherwise, render as a fixed fullscreen overlay
  const containerClass = isLiteMode 
    ? "flex flex-col bg-white" 
    : "fixed inset-0 z-50 bg-white flex flex-col overflow-hidden";
  
  return (
    <div className={containerClass}>
      {/* Image Cropper Overlay */}
      {showCropper && cropperImage && (
        <ImageCropper 
          image={cropperImage} 
          onCrop={handleCropComplete} 
          onCancel={handleCropCancel} 
        />
      )}
      
      {/* Loading overlay for edit mode */}
      {isLoadingListing && (
        <div className="absolute inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading listing...</p>
          </div>
        </div>
      )}
      
      {/* Header - Skip when isLiteMode (already in LiteLayout) */}
      {!isLiteMode && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white shrink-0">
          <button 
            onClick={editMode ? () => navigate('/listings') : onClose} 
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700"
          >
            {editMode ? <ArrowLeft size={24} /> : <X size={24} />}
          </button>
          <h2 className="font-semibold text-gray-900">
            {editMode ? 'Edit Listing' : 'Add New Listing'}
          </h2>
          <div className="w-10" />
        </div>
      )}

      {/* Scrollable Content */}
      <div className={`flex-1 ${isLiteMode ? '' : 'overflow-y-auto'}`}>
        <div className={`${isLiteMode ? '' : 'p-4'} space-y-5`}>
          
          {/* Listing Type Selector - Hidden in Edit Mode */}
          {!editMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Listing Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {LISTING_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = listingType === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setListingType(type.id)}
                      className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={20} className={isSelected ? 'text-indigo-600' : 'text-gray-400'} />
                      <span className="text-[10px] mt-1 font-medium truncate w-full text-center">
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Show listing type badge in edit mode */}
          {editMode && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
              {(() => {
                const type = LISTING_TYPES.find(t => t.id === listingType) || LISTING_TYPES[0];
                const Icon = type.icon;
                return (
                  <>
                    <Icon size={18} className="text-indigo-600" />
                    <span className="text-sm font-medium text-gray-700">{type.label}</span>
                    <span className="text-xs text-gray-400 ml-auto">Type cannot be changed</span>
                  </>
                );
              })()}
            </div>
          )}

          {/* Category Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={formData.category}
                onChange={(e) => {
                  const selectedCat = categories.find(c => c.id === e.target.value);
                  setFormData(prev => ({ ...prev, category: e.target.value, categoryName: selectedCat?.name || '' }));
                  clearFieldError('category');
                }}
                disabled={loadingCategories}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${
                  hasError('category') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Subcategory Select (conditional) */}
          {subcategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subcategory
              </label>
              <div className="relative">
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select subcategory (optional)</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* iter-344 — Country of Origin + Weight, same row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country of Origin <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  data-testid="country-of-origin-select"
                  value={formData.country_of_origin}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, country_of_origin: e.target.value }));
                    clearFieldError('country_of_origin');
                  }}
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    hasError('country_of_origin') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  {COUNTRY_OF_ORIGIN_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              {hasError('country_of_origin') && (
                <p className="text-xs text-red-500 mt-1" data-testid="country-of-origin-error">Country of Origin is required</p>
              )}
            </div>
            {typeConfig.showShipping && formData.requires_shipping && (
              <div data-testid="weight-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    data-testid="weight-input"
                    value={formData.weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                    placeholder="0.00"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                    min="0"
                    step="0.01"
                  />
                  <select
                    data-testid="weight-unit-select"
                    value={formData.weight_unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight_unit: e.target.value }))}
                    className="w-20 px-2 py-3 bg-gray-50 border border-gray-200 rounded-xl appearance-none text-center font-medium"
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                    <option value="oz">oz</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* iter-331 — Business / Trade / Regulatory Licenses & Certificates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Licenses & Certificates
              {requiredLicenseType && <span className="text-red-500"> *</span>}
              {!requiredLicenseType && <span className="text-xs text-gray-400 ml-1">(optional)</span>}
            </label>
            {requiredLicenseType && (
              <p className="text-xs text-red-600 mb-2" data-testid="license-required-hint">
                {LICENSE_TYPE_LABELS[requiredLicenseType] || requiredLicenseType} number is required for this category
              </p>
            )}
            <div className="space-y-2" data-testid="licenses-certifications-list">
              {(formData.licenses_certifications || []).map((lic, idx) => {
                const isLocked = requiredLicenseType && lic.type === requiredLicenseType;
                return (
                  <div key={idx} data-testid={`license-row-wrap-${idx}`}>
                  <div className="flex gap-2" data-testid={`license-row-${idx}`}>
                    <select
                      value={lic.type}
                      disabled={isLocked}
                      onChange={(e) => {
                        const next = [...formData.licenses_certifications];
                        next[idx] = applySavedDefault({ ...next[idx], type: e.target.value, image_url: e.target.value === 'Other' ? next[idx].image_url : '' });
                        setFormData(prev => ({ ...prev, licenses_certifications: next }));
                      }}
                      className="w-32 px-2 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm disabled:opacity-70"
                      data-testid={`license-type-select-${idx}`}
                    >
                      {LICENSE_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={lic.number}
                      onChange={(e) => {
                        const next = [...formData.licenses_certifications];
                        next[idx] = { ...next[idx], number: e.target.value };
                        setFormData(prev => ({ ...prev, licenses_certifications: next }));
                        clearFieldError('licenses_certifications');
                      }}
                      placeholder={isLocked ? `${lic.type} number (required)` : `${lic.type} number`}
                      className={`flex-1 px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        hasError('licenses_certifications') && isLocked ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                      data-testid={`license-number-input-${idx}`}
                    />
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() => {
                          // iter-374 — removing an inherited (vendor-default)
                          // row must also mark that type "excluded" for THIS
                          // listing, otherwise the vendor's next Store Profile
                          // license save silently re-adds it. Mirrors
                          // ComplianceCard.jsx::removeLicenseRow.
                          setFormData(prev => {
                            const row = prev.licenses_certifications[idx];
                            const nextExcluded = (row?.source === 'vendor_default' && row?.type)
                              ? (prev.excluded_license_types || []).includes(row.type)
                                ? prev.excluded_license_types
                                : [...(prev.excluded_license_types || []), row.type]
                              : (prev.excluded_license_types || []);
                            return {
                              ...prev,
                              licenses_certifications: prev.licenses_certifications.filter((_, i) => i !== idx),
                              excluded_license_types: nextExcluded,
                            };
                          });
                        }}
                        className="px-3 text-gray-400 hover:text-red-500"
                        data-testid={`license-remove-btn-${idx}`}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  {/* iter-344 — Vendor-uploaded certificate photo/scan (extra PDP trust) */}
                  <div className="flex items-center gap-2 mt-1">
                    {lic.image_url ? (
                      <div className="flex items-center gap-2" data-testid={`license-cert-preview-${idx}`}>
                        <img src={lic.image_url} alt="Certificate" className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...formData.licenses_certifications];
                            next[idx] = { ...next[idx], image_url: '' };
                            setFormData(prev => ({ ...prev, licenses_certifications: next }));
                          }}
                          className="text-xs text-red-500"
                          data-testid={`license-cert-remove-btn-${idx}`}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="text-xs text-indigo-600 font-medium flex items-center gap-1 cursor-pointer" data-testid={`license-cert-upload-btn-${idx}`}>
                        <Upload size={14} />
                        {certUploadingIdx === idx ? 'Uploading...' : 'Upload Certificate Photo (optional)'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={certUploadingIdx === idx}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadLicenseCertImage(idx, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                  </div>
                );
              })}
            </div>
            {hasError('licenses_certifications') && (
              <p className="text-xs text-red-500 mt-1" data-testid="licenses-certifications-error">
                {validationErrors.find(e => e.field === 'licenses_certifications')?.message}
              </p>
            )}
            <button
              type="button"
              onClick={() => setFormData(prev => ({
                ...prev,
                licenses_certifications: [...(prev.licenses_certifications || []), applySavedDefault({ type: requiredLicenseType || 'FSSAI', number: '' })],
              }))}
              className="mt-2 text-sm text-indigo-600 font-medium flex items-center gap-1"
              data-testid="add-license-btn"
            >
              <Plus size={16} /> Add License
            </button>
          </div>

          {/* iter-355 — Buying Mode (Cart vs Enquiry) for physical products.
              Ported from the desktop ComplianceCard (iter-350) which never
              got mirrored into this mobile Lite Add Item overlay — the
              missing "Buying option" field vendors reported. */}
          {listingType === 'product' && (
            <div data-testid="product-buying-mode-section">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buying Mode
              </label>
              {complianceConfig.buying_mode_override_enabled ? (
                <div
                  className="mb-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2"
                  data-testid="buying-mode-locked-banner"
                >
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Locked to <strong>{complianceConfig.buying_mode_override_type === 'cart' ? 'Cart / Buy Now' : 'Enquiry Only (WhatsApp)'}</strong> by admin
                    policy for this category. Your selection below is saved and applies once lifted.
                  </span>
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="product-buying-mode-cart"
                  disabled={complianceConfig.buying_mode_override_enabled}
                  onClick={() => setFormData(prev => ({ ...prev, bookingMode: 'instant' }))}
                  className={`p-3 rounded-xl border-2 text-center transition-all disabled:opacity-50 ${
                    formData.bookingMode !== 'enquiry'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Package size={20} className="mx-auto mb-1 text-indigo-600" />
                  <span className="text-xs font-medium block">Cart / Buy Now</span>
                </button>
                <button
                  type="button"
                  data-testid="product-buying-mode-enquiry"
                  disabled={complianceConfig.buying_mode_override_enabled}
                  onClick={() => setFormData(prev => ({ ...prev, bookingMode: 'enquiry' }))}
                  className={`p-3 rounded-xl border-2 text-center transition-all disabled:opacity-50 ${
                    formData.bookingMode === 'enquiry'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <ScrollText size={20} className="mx-auto mb-1 text-indigo-600" />
                  <span className="text-xs font-medium block">Enquiry Only</span>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                "Enquiry Only" hides Add to Cart / Buy Now on the product page and shows Enquire Now + Chat on WhatsApp instead.
              </p>
            </div>
          )}

          {/* iter-332/344 — Shelf-Life + Freshness Guarantee, same row (perishable categories) */}
          {complianceConfig.enable_expiry_rules && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {complianceConfig.expiry_legal_label} <span className="text-red-500">*</span>
                </label>
                {complianceConfig.expiry_date_format === 'exact_date' ? (
                  <input
                    type="date"
                    data-testid="expiry-date-input"
                    value={formData.expiry_date}
                    onChange={(e) => { setFormData(prev => ({ ...prev, expiry_date: e.target.value })); clearFieldError('expiry_date'); }}
                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      hasError('expiry_date') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                ) : (
                  <input
                    type="number"
                    min="0"
                    data-testid="shelf-life-days-input"
                    value={formData.shelf_life_days}
                    onChange={(e) => { setFormData(prev => ({ ...prev, shelf_life_days: e.target.value })); clearFieldError('shelf_life_days'); }}
                    placeholder={complianceConfig.min_guaranteed_shelf_life_days ? `Min ${complianceConfig.min_guaranteed_shelf_life_days} days` : 'Shelf life in days'}
                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      hasError('shelf_life_days') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                )}
                {(hasError('expiry_date') || hasError('shelf_life_days')) && (
                  <p className="text-xs text-red-500 mt-1" data-testid="expiry-error">
                    {validationErrors.find(e => e.field === 'expiry_date' || e.field === 'shelf_life_days')?.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Freshness Guarantee on Delivery
                </label>
                <input
                  type="number"
                  min={shelfLifeFloorDays || 0}
                  data-testid="freshness-guarantee-input"
                  value={formData.freshness_guarantee_days}
                  onChange={(e) => { setFormData(prev => ({ ...prev, freshness_guarantee_days: e.target.value })); clearFieldError('freshness_guarantee_days'); }}
                  placeholder={shelfLifeFloorDays ? `Min ${shelfLifeFloorDays}+ days` : 'e.g. 90 days'}
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    hasError('freshness_guarantee_days') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {shelfLifeGuaranteeWording ? (
                  <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 mt-1.5" data-testid="freshness-guarantee-wording">
                    <Shield size={13} className="shrink-0" />
                    Minimum Guaranteed Shelf Life at delivery: {shelfLifeGuaranteeWording}
                    {complianceConfig.min_guaranteed_shelf_life_type === 'percent' && shelfLifeFloorDays ? ` (≈ ${shelfLifeFloorDays} days for this listing)` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Optional — days of freshness you guarantee remain when delivered.</p>
                )}
                {hasError('freshness_guarantee_days') && (
                  <p className="text-xs text-red-500 mt-1" data-testid="freshness-guarantee-error">
                    {validationErrors.find(e => e.field === 'freshness_guarantee_days')?.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* iter-332 — Warranty / Guarantee (non-perishable categories) */}
          {complianceConfig.enable_warranty_rules && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Warranty / Guarantee <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  data-testid="warranty-assurance-type-select"
                  value={formData.warranty_assurance_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, warranty_assurance_type: e.target.value }))}
                  className="flex-1 px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm"
                >
                  <option value="manufacturer_warranty">Manufacturer Warranty</option>
                  <option value="seller_warranty">Seller Warranty</option>
                  <option value="brand_guarantee">Brand Guarantee</option>
                  <option value="none">None</option>
                </select>
                <input
                  type="number"
                  min="0"
                  data-testid="warranty-duration-value-input"
                  value={formData.warranty_duration_value}
                  onChange={(e) => { setFormData(prev => ({ ...prev, warranty_duration_value: e.target.value })); clearFieldError('warranty_duration_value'); }}
                  placeholder="Duration"
                  className={`w-24 px-3 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    hasError('warranty_duration_value') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                <select
                  data-testid="warranty-duration-unit-select"
                  value={formData.warranty_duration_unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, warranty_duration_unit: e.target.value }))}
                  className="w-28 px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm"
                >
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
              {hasError('warranty_duration_value') && (
                <p className="text-xs text-red-500 mt-1" data-testid="warranty-error">
                  {validationErrors.find(e => e.field === 'warranty_duration_value')?.message}
                </p>
              )}
            </div>
          )}

          {/* iter-332 — Return/Exchange Window trust badge (informational, admin-set) */}
          {complianceConfig.enable_return_window && complianceConfig.return_window_days && (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800" data-testid="return-window-badge">
              <RotateCcw size={16} />
              {complianceConfig.return_window_days} Days Return / Exchange Window applies to this category
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
              <span className="text-xs text-gray-400 ml-1">(min 7 chars)</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, title: e.target.value }));
                clearFieldError('title');
              }}
              placeholder={`Enter ${typeConfig.label.toLowerCase()} title`}
              className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                hasError('title') ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
              maxLength={80}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{formData.title.length}/80</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
              <span className="text-xs text-gray-400 ml-1">(min 15 chars)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, description: e.target.value }));
                clearFieldError('description');
              }}
              placeholder={`Describe your ${typeConfig.label.toLowerCase()} in detail...`}
              rows={3}
              className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none ${
                hasError('description') ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {formData.description.length}/2000
            </p>
          </div>

          {/* Image Upload - MULTI-IMAGE SUPPORT */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photos <span className="text-red-500">*</span>
              <span className="text-xs text-gray-400 ml-1">({images.length}/10)</span>
            </label>
            
            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageSelect}
              className="hidden"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="hidden"
            />
            
            {/* Horizontal scrollable image gallery */}
            <div className={`rounded-xl ${hasError('image') ? 'ring-2 ring-red-300' : ''}`}>
              <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {/* Existing images */}
                {images.map((img, index) => (
                  <div 
                    key={img.id}
                    className="relative flex-shrink-0 w-24 h-24"
                  >
                    <img 
                      src={img.preview} 
                      alt={`Image ${index + 1}`}
                      className={`w-full h-full object-cover rounded-xl border-2 ${
                        img.uploadStatus === 'failed' ? 'border-red-400' :
                        img.uploadStatus === 'completed' ? 'border-green-400' :
                        'border-gray-200'
                      }`}
                    />
                    
                    {/* Upload Status Overlay */}
                    {!img.isExisting && img.uploadStatus !== 'completed' && (
                      <div className={`absolute inset-0 rounded-xl flex items-center justify-center ${
                        img.uploadStatus === 'uploading' ? 'bg-black/30' :
                        img.uploadStatus === 'failed' ? 'bg-red-500/30' :
                        'bg-black/20'
                      }`}>
                        {img.uploadStatus === 'uploading' && (
                          <Loader2 size={20} className="text-white animate-spin" />
                        )}
                        {img.uploadStatus === 'failed' && (
                          <button
                            type="button"
                            onClick={() => retryImageUpload(img.id)}
                            className="p-1.5 bg-red-500 text-white rounded-full"
                            title={img.uploadError || 'Upload failed - tap to retry'}
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                        {img.uploadStatus === 'pending' && (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                    )}
                    
                    {/* Upload Complete Checkmark */}
                    {!img.isExisting && img.uploadStatus === 'completed' && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center">
                        <CheckCircle size={12} />
                      </div>
                    )}
                    
                    {/* Primary badge for first image */}
                    {index === 0 && (
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] rounded font-medium">
                        Primary
                      </div>
                    )}
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                    >
                      <X size={14} />
                    </button>
                    {/* Crop button - only show when upload complete or existing */}
                    {(img.isExisting || img.uploadStatus === 'completed') && (
                      <button
                        type="button"
                        onClick={() => openCropperForImage(img.id)}
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gray-800 text-white text-[10px] rounded-full flex items-center gap-1 shadow-md"
                      >
                        <Crop size={10} />
                        Crop
                      </button>
                    )}
                  </div>
                ))}
                
                {/* Add more images buttons */}
                {images.length < 10 && (
                  <>
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-dashed border-indigo-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-indigo-400 transition-colors"
                    >
                      <Camera size={24} className="text-indigo-500" />
                      <span className="text-xs font-medium text-indigo-600">Camera</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-gray-400 transition-colors"
                    >
                      <ImageIcon size={24} className="text-gray-500" />
                      <span className="text-xs font-medium text-gray-600">Gallery</span>
                    </button>
                  </>
                )}
              </div>
              
              {/* Helper text */}
              {images.length === 0 && (
                <>
                  {listingType === 'service' && isProfessionalServicesCategory(formData.categoryName) ? (
                    <div className="mt-2 p-2 bg-indigo-50 rounded-lg">
                      <p className="text-xs text-indigo-700 text-center">
                        <Briefcase size={12} className="inline mr-1" />
                        Image optional for Professional Services - we&apos;ll use a default icon based on your subcategory
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2 text-center">
                      Tap Gallery to select multiple images at once
                    </p>
                  )}
                </>
              )}
              {images.length > 0 && images.length < 10 && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ {images.length} photo{images.length > 1 ? 's' : ''} added • Scroll to see more or add up to {10 - images.length} more
                </p>
              )}
            </div>
          </div>

          {/* ============ SERVICE-SPECIFIC FIELDS ============ */}
          {listingType === 'service' && (
            <>
              {/* Booking Mode */}
              <div data-testid="booking-mode-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How can customers book?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    data-testid="booking-mode-instant"
                    onClick={() => setFormData(prev => ({ ...prev, bookingMode: 'instant' }))}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      formData.bookingMode === 'instant'
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Calendar size={20} className="mx-auto mb-1 text-indigo-600" />
                    <span className="text-xs font-medium block">Instant Book</span>
                    <span className="text-[10px] text-gray-400">Confirm right away</span>
                  </button>
                  <button
                    type="button"
                    data-testid="booking-mode-request"
                    onClick={() => setFormData(prev => ({ ...prev, bookingMode: 'request' }))}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      formData.bookingMode === 'request'
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Clock size={20} className="mx-auto mb-1 text-indigo-600" />
                    <span className="text-xs font-medium block">Request Only</span>
                    <span className="text-[10px] text-gray-400">You approve each</span>
                  </button>
                  <button
                    type="button"
                    data-testid="booking-mode-enquiry"
                    onClick={() => setFormData(prev => ({ ...prev, bookingMode: 'enquiry' }))}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      formData.bookingMode === 'enquiry'
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <ScrollText size={20} className="mx-auto mb-1 text-indigo-600" />
                    <span className="text-xs font-medium block">Enquiry Only</span>
                    <span className="text-[10px] text-gray-400">Customers enquire</span>
                  </button>
                </div>
              </div>

              {/* Service Location Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={14} className="inline mr-1" />
                  Where do you provide this service?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {SERVICE_LOCATIONS.map(loc => (
                    <button
                      key={loc.value}
                      type="button"
                      data-testid={`service-loc-${loc.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, service_location: loc.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.service_location === loc.value
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl block mb-1">{loc.icon}</span>
                      <span className="text-xs font-medium">{loc.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Online: Meeting Link */}
              {formData.service_location === 'online' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Link2 size={14} className="inline mr-1" />
                    Meeting Link
                  </label>
                  <input
                    type="url"
                    data-testid="meeting-link-input"
                    value={formData.meeting_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, meeting_link: e.target.value }))}
                    placeholder="https://zoom.us/j/... or Google Meet link"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              )}

              {/* My Location (at_provider): Full address + City */}
              {formData.service_location === 'provider_location' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      data-testid="service-address-input"
                      value={formData.service_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, service_address: e.target.value }))}
                      placeholder="Enter your full business address"
                      rows={2}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-sm resize-none ${
                        hasError('service_address') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      data-testid="service-city-input"
                      value={formData.service_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, service_city: e.target.value }))}
                      placeholder="e.g., Mumbai, Delhi"
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl ${
                        hasError('service_city') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                </div>
              )}

              {/* I Visit Customer: City + Travel Radius */}
              {formData.service_location === 'customer_location' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      data-testid="service-city-input"
                      value={formData.service_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, service_city: e.target.value }))}
                      placeholder="e.g., Mumbai, Delhi"
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl ${
                        hasError('service_city') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Travel Radius (km)
                    </label>
                    <input
                      type="number"
                      data-testid="travel-radius-input"
                      value={formData.travel_radius}
                      onChange={(e) => setFormData(prev => ({ ...prev, travel_radius: e.target.value }))}
                      placeholder="e.g., 20"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                      min="1"
                    />
                    <p className="text-xs text-gray-400 mt-1">How far you're willing to travel</p>
                  </div>
                </div>
              )}

              {/* Service Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock size={14} className="inline mr-1" />
                  Service Duration
                </label>
                <div className="relative">
                  <select
                    data-testid="service-duration-select"
                    value={formData.service_duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, service_duration: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl appearance-none"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                    <option value="240">4 hours</option>
                    <option value="480">Full day</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Inclusions & Exclusions */}
              <div className="space-y-3" data-testid="inclusions-exclusions-section">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CheckSquare size={14} className="inline mr-1" />
                    What's Included
                  </label>
                  <IconTagInput
                    tags={formData.inclusions}
                    onChange={(tags) => setFormData(prev => ({ ...prev, inclusions: tags }))}
                    placeholder="e.g., Equipment, Materials, Consultation..."
                    testId="inclusions-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <XCircle size={14} className="inline mr-1" />
                    What's Not Included
                  </label>
                  <IconTagInput
                    tags={formData.exclusions}
                    onChange={(tags) => setFormData(prev => ({ ...prev, exclusions: tags }))}
                    placeholder="e.g., Travel costs, Raw materials..."
                    testId="exclusions-input"
                  />
                </div>
              </div>

              {/* Service Availability Schedule */}
              {formData.bookingMode !== 'enquiry' && (
              <div className={`p-4 bg-gray-50 rounded-xl ${hasError('availability') ? 'ring-2 ring-red-300' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Calendar size={14} className="inline mr-1" />
                  Availability Schedule {formData.bookingMode !== 'enquiry' && <span className="text-red-500">*</span>}
                </label>
                <LiteAvailabilityScheduler
                  value={availability}
                  onChange={setAvailability}
                  listingType="service"
                  showDuration={true}
                />
              </div>
              )}
            </>
          )}

          {/* ============ RENTAL-SPECIFIC FIELDS ============ */}
          {listingType === 'rental' && (
            <>
              {/* Rental Booking Mode — hide for venue (always request) */}
              {formData.rental_template !== 'venue_booking' && (
              <div data-testid="rental-booking-mode-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How can customers book?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'instant', label: 'Instant Book', hint: 'Confirm right away' },
                    { value: 'request', label: 'Request Only', hint: 'You approve each' },
                    { value: 'enquiry', label: 'Enquiry Only', hint: 'Customers enquire' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      data-testid={`rental-booking-mode-${opt.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, rental_booking_mode: opt.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.rental_booking_mode === opt.value
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xs font-medium block">{opt.label}</span>
                      <span className="text-[10px] text-gray-400">{opt.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Rental Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rental Period
                </label>
                <div className="relative">
                  <select
                    value={formData.rental_period}
                    onChange={(e) => setFormData(prev => ({ ...prev, rental_period: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl appearance-none"
                  >
                    <option value="daily">Daily Rate</option>
                    <option value="weekly">Weekly Rate</option>
                    <option value="monthly">Monthly Rate</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Security Deposit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Security Deposit (₹)
                  <span className="text-xs text-gray-400 ml-1">(Optional, 0 if none)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                  <input
                    type="number"
                    value={formData.security_deposit}
                    onChange={(e) => setFormData(prev => ({ ...prev, security_deposit: e.target.value }))}
                    placeholder="0"
                    className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                    min="0"
                  />
                </div>
              </div>

              {/* Minimum Rental Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Rental Period
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.min_rental_period}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_rental_period: e.target.value }))}
                    placeholder="1"
                    className="w-24 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                    min="1"
                  />
                  <div className="relative flex-1">
                    <select
                      value={formData.min_rental_unit}
                      onChange={(e) => setFormData(prev => ({ ...prev, min_rental_unit: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl appearance-none"
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                    <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Pickup Location */}
              <div className={`p-4 bg-gray-50 rounded-xl ${(hasError('rental_address') || hasError('rental_city')) ? 'ring-2 ring-red-300' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <MapPin size={14} className="inline mr-1" />
                  {formData.rental_template === 'venue_booking' ? 'Venue Location' : 'Pickup Location'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formData.rental_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, rental_address: e.target.value }))}
                    placeholder={formData.rental_template === 'venue_booking' ? 'Full venue address' : 'Full address'}
                    className={`w-full px-4 py-3 bg-white border rounded-xl ${
                      hasError('rental_address') ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={formData.rental_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, rental_city: e.target.value }))}
                      placeholder="City"
                      className={`px-4 py-3 bg-white border rounded-xl ${
                        hasError('rental_city') ? 'border-red-300' : 'border-gray-200'
                      }`}
                    />
                    <input
                      type="text"
                      value={formData.rental_pincode}
                      onChange={(e) => setFormData(prev => ({ ...prev, rental_pincode: e.target.value }))}
                      placeholder="Pincode"
                      className="px-4 py-3 bg-white border border-gray-200 rounded-xl"
                    />
                  </div>
                  {/* Venue-specific extra fields */}
                  {formData.rental_template === 'venue_booking' && (
                    <>
                      <input type="text" data-testid="venue-nearest-landmark"
                        value={formData.venue_nearest_landmark}
                        onChange={(e) => setFormData(prev => ({ ...prev, venue_nearest_landmark: e.target.value }))}
                        placeholder="Nearest landmark (e.g., Near City Mall)"
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm"
                      />
                      <input type="text" data-testid="venue-nearest-transport"
                        value={formData.venue_nearest_transport}
                        onChange={(e) => setFormData(prev => ({ ...prev, venue_nearest_transport: e.target.value }))}
                        placeholder="Nearest metro / bus stop"
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm"
                      />
                      <div className="flex items-center gap-3">
                        <button type="button" data-testid="venue-parking-toggle"
                          onClick={() => setFormData(prev => ({ ...prev, venue_parking_available: !prev.venue_parking_available }))}
                          className={`w-11 h-6 rounded-full transition-colors ${formData.venue_parking_available ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.venue_parking_available ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-sm text-gray-700">Parking available</span>
                      </div>
                      <input type="url" data-testid="venue-maps-link"
                        value={formData.venue_google_maps_link}
                        onChange={(e) => setFormData(prev => ({ ...prev, venue_google_maps_link: e.target.value }))}
                        placeholder="Google Maps link (optional)"
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Rental Availability */}
              <div className={`p-4 bg-gray-50 rounded-xl ${hasError('availability') ? 'ring-2 ring-red-300' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📅 Availability Schedule <span className="text-red-500">*</span>
                </label>
                <LiteAvailabilityScheduler
                  value={availability}
                  onChange={setAvailability}
                  listingType="rental"
                  showDuration={false}
                />
              </div>
            </>
          )}

          {/* ============ EVENT-SPECIFIC FIELDS ============ */}
          {listingType === 'event' && (
            <>
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl ${
                      hasError('event_date') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_time: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
              </div>

              {/* Organizer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users size={14} className="inline mr-1" />
                  Organizer Name
                </label>
                <input
                  type="text"
                  data-testid="organizer-name-input"
                  value={formData.organizer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizer_name: e.target.value }))}
                  placeholder="e.g., ABC Events, Your Store Name"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                />
              </div>

              {/* Recurring Event */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900 text-sm">Recurring Event</span>
                    <p className="text-xs text-gray-500">This event repeats on a schedule</p>
                  </div>
                  <button
                    type="button"
                    data-testid="recurring-toggle"
                    onClick={() => setFormData(prev => ({ ...prev, is_recurring: !prev.is_recurring }))}
                    className={`w-12 h-7 rounded-full transition-colors ${
                      formData.is_recurring ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      formData.is_recurring ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {formData.is_recurring && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                    <div className="relative">
                      <select
                        data-testid="recurrence-frequency-select"
                        value={formData.recurrence_frequency}
                        onChange={(e) => setFormData(prev => ({ ...prev, recurrence_frequency: e.target.value }))}
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm appearance-none"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>

              {/* Event Inclusions & Exclusions */}
              <div className="space-y-3" data-testid="event-inclusions-exclusions-section">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CheckSquare size={14} className="inline mr-1" />
                    What&apos;s Included
                  </label>
                  <IconTagInput
                    tags={formData.event_inclusions}
                    onChange={(tags) => setFormData(prev => ({ ...prev, event_inclusions: tags }))}
                    placeholder="e.g., Lunch, T-shirt, Certificates..."
                    testId="event-inclusions-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <XCircle size={14} className="inline mr-1" />
                    What&apos;s Not Included
                  </label>
                  <IconTagInput
                    tags={formData.event_exclusions}
                    onChange={(tags) => setFormData(prev => ({ ...prev, event_exclusions: tags }))}
                    placeholder="e.g., Accommodation, Transport..."
                    testId="event-exclusions-input"
                  />
                </div>
              </div>
            </>
          )}

          {/* ============ SUBSCRIPTION-SPECIFIC FIELDS ============ */}
          {listingType === 'subscription' && (
            <div className="p-4 bg-gray-50 rounded-xl space-y-4" data-testid="subscription-delivery-section">
              <div className="flex items-center gap-2 mb-1">
                <Truck size={16} className="text-indigo-600" />
                <span className="font-medium text-gray-900 text-sm">Delivery Coverage</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'area', label: 'City / Area' },
                  { value: 'radius', label: 'Delivery Radius' },
                  { value: 'pincodes', label: 'Pincodes' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    data-testid={`delivery-type-${opt.value}`}
                    onClick={() => setFormData(prev => ({ ...prev, delivery_type: opt.value }))}
                    className={`py-2 px-3 text-xs font-medium rounded-xl border-2 transition-all ${
                      formData.delivery_type === opt.value
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {formData.delivery_type === 'radius' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Dispatch Location</label>
                    <input type="text"
                      data-testid="delivery-center-address-input"
                      value={formData.delivery_center_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, delivery_center_address: e.target.value }))}
                      placeholder="e.g., Andheri East, Mumbai"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Radius (km)</label>
                    <input type="number"
                      data-testid="delivery-radius-input"
                      value={formData.delivery_radius}
                      onChange={(e) => setFormData(prev => ({ ...prev, delivery_radius: e.target.value }))}
                      placeholder="e.g., 10"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                      min="1"
                    />
                  </div>
                </div>
              )}
              {formData.delivery_type === 'pincodes' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Serviceable Pincodes</label>
                  <textarea
                    data-testid="delivery-pincodes-input"
                    value={formData.delivery_pincodes}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_pincodes: e.target.value }))}
                    placeholder="e.g., 400001, 400002, 400050"
                    rows={2}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Separate multiple pincodes with commas</p>
                </div>
              )}
              {formData.delivery_type === 'area' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City / Service Area</label>
                  <input type="text"
                    data-testid="delivery-area-input"
                    value={formData.delivery_center_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_center_address: e.target.value }))}
                    placeholder="e.g., Mumbai, Pune, Delhi NCR"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {/* ============ PRICE AND STOCK ROW ============ */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {typeConfig.priceLabel} {listingType !== 'event' && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                <input
                  type="number"
                  data-testid="price-input"
                  value={formData.price}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, price: e.target.value }));
                    clearFieldError('price');
                  }}
                  placeholder={typeConfig.pricePlaceholder || "0.00"}
                  className={`w-full pl-8 pr-4 py-3 bg-gray-50 border rounded-xl ${
                    hasError('price') ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            
            {typeConfig.showStock && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Qty
                </label>
                <input
                  type="number"
                  data-testid="stock-input"
                  value={formData.stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                  placeholder="1"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl"
                  min="1"
                />
              </div>
            )}
          </div>

          {/* Charge TAX/GST Checkbox (prominent, not hidden in advanced) */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <div>
              <span className="font-medium text-gray-900" data-testid="charge-tax-label">Taxes charged additionally on top of price</span>
              <p className="text-xs text-gray-500">Include tax in product pricing</p>
            </div>
            <button
              type="button"
              data-testid="charge-tax-toggle"
              onClick={() => setFormData(prev => ({ ...prev, charge_tax: !prev.charge_tax }))}
              className={`w-12 h-7 rounded-full transition-colors ${
                formData.charge_tax ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  formData.charge_tax ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Shipping Toggle (for products/subscriptions) */}
          {typeConfig.showShipping && (
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
              <div>
                <span className="font-medium text-gray-900">Requires Shipping</span>
                <p className="text-xs text-gray-500">Physical delivery needed</p>
              </div>
              <button
                type="button"
                data-testid="requires-shipping-toggle"
                onClick={() => setFormData(prev => ({ ...prev, requires_shipping: !prev.requires_shipping }))}
                className={`w-12 h-7 rounded-full transition-colors ${
                  formData.requires_shipping ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span 
                  className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    formData.requires_shipping ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Digital Product Section (when shipping is OFF) */}
          {typeConfig.showShipping && !formData.requires_shipping && (
            <div className="p-4 bg-blue-50 rounded-xl space-y-3" data-testid="digital-product-section">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">Digital Product?</span>
                  <p className="text-xs text-gray-500">Provide a download URL after purchase</p>
                </div>
                <button
                  type="button"
                  data-testid="digital-product-toggle"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    product_type: prev.product_type === 'digital' ? 'physical' : 'digital' 
                  }))}
                  className={`w-12 h-7 rounded-full transition-colors ${
                    formData.product_type === 'digital' ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span 
                    className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      formData.product_type === 'digital' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {formData.product_type === 'digital' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Download URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    data-testid="digital-download-url"
                    value={formData.digital_download_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, digital_download_url: e.target.value }))}
                    placeholder="https://drive.google.com/... or file hosting link"
                    className="w-full px-4 py-3 bg-white border border-blue-200 rounded-xl text-sm"
                  />
                  <p className="text-xs text-blue-600 mt-1">Link shared with customers after payment</p>
                </div>
              )}
            </div>
          )}

          {/* ============ ADVANCED DETAILS TOGGLE ============ */}
          <div className="border-t border-gray-200 pt-4 mt-2">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between py-3 px-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-all"
            >
              <div className="flex items-center gap-3">
                <Settings2 size={20} className="text-slate-600" />
                <div className="text-left">
                  <span className="font-medium text-gray-900 block">Advanced Details</span>
                  <span className="text-xs text-gray-500">Variants, Custom Options, HSN/SAC, SEO</span>
                </div>
              </div>
              {showAdvanced ? (
                <ChevronUp size={20} className="text-gray-400" />
              ) : (
                <ChevronDown size={20} className="text-gray-400" />
              )}
            </button>
            
            {/* Advanced Details Content */}
            {showAdvanced && (
              <div className="mt-4 space-y-4">
                
                {/* ============ VARIANTS (Product, Subscription, AND Service) ============ */}
                {(listingType === 'product' || listingType === 'subscription' || listingType === 'service') && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                          <Layers size={20} className="text-indigo-600" />
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 block">
                            {listingType === 'service' ? 'Service Packages' : 'Product Variants'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {listingType === 'service' ? 'Basic, Premium, Combo packages' : 'Size, Color, Material options'}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="variants-toggle"
                        onClick={() => setAdvancedData(prev => ({ ...prev, has_variants: !prev.has_variants }))}
                        className={`w-12 h-7 rounded-full transition-colors ${
                          advancedData.has_variants ? 'bg-indigo-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          advancedData.has_variants ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    {advancedData.has_variants && (
                      <div className="border-t border-gray-100 pt-4">
                        <VariantManagerV2
                          variants={advancedData.variants}
                          variantCombinations={advancedData.variantCombinations}
                          basePrice={parseFloat(formData.price) || 0}
                          listingType={listingType}
                          onVariantsChange={(variants) => setAdvancedData(prev => ({ ...prev, variants }))}
                          onCombinationsChange={(variantCombinations) => setAdvancedData(prev => ({ ...prev, variantCombinations }))}
                          onTotalStockChange={(totalStock) => {
                          }}
                          maxVariants={3}
                          maxOptions={10}
                          hideHeader={false}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* ============ CUSTOM OPTIONS ============ */}
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                        <Plus size={20} className="text-purple-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block">Custom Options / Add-ons</span>
                        <span className="text-xs text-gray-500">Gift wrap, personalization, extras</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="custom-options-toggle"
                      onClick={() => setAdvancedData(prev => ({ ...prev, has_custom_options: !prev.has_custom_options }))}
                      className={`w-12 h-7 rounded-full transition-colors ${
                        advancedData.has_custom_options ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        advancedData.has_custom_options ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {advancedData.has_custom_options && (
                    <div className="border-t border-gray-100 pt-4">
                      <CustomOptionsConfig
                        categoryId={formData.category}
                        subcategoryId={formData.subcategory}
                        listingId={listingId}
                        basePrice={parseFloat(formData.price) || 0}
                        initialOptions={advancedData.customOptionsConfig?.options || []}
                        onConfigChange={(config) => setAdvancedData(prev => ({ 
                          ...prev, 
                          customOptionsConfig: config 
                        }))}
                      />
                    </div>
                  )}
                </div>
                
                {/* ============ TAX DETAILS (HSN/SAC & Rate) ============ */}
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText size={18} className="text-emerald-600" />
                    <span className="font-medium text-gray-900">Tax Details</span>
                    {!formData.charge_tax && (
                      <span className="text-xs text-gray-400 ml-auto">Enable "Charge TAX/GST" above first</span>
                    )}
                  </div>
                  {formData.charge_tax && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">HSN/SAC Code</label>
                        <input type="text" data-testid="hsn-code-input"
                          value={advancedData.hsn_code}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, hsn_code: e.target.value }))}
                          placeholder={listingType === 'service' ? 'e.g., 998314' : 'e.g., 6109'}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tax Rate (%)</label>
                        <input type="number" data-testid="tax-rate-input"
                          value={advancedData.tax_rate}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, tax_rate: e.target.value }))}
                          placeholder="e.g., 18"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                          min="0" max="100" />
                      </div>
                    </div>
                  )}
                </div>

                {/* ============ PRODUCT: SERVICEABLE AREAS ============ */}
                {listingType === 'product' && formData.requires_shipping && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="serviceable-areas-section">
                    <div className="flex items-center gap-3 mb-3">
                      <Globe size={18} className="text-blue-600" />
                      <span className="font-medium text-gray-900">Delivery Coverage</span>
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'state', label: 'Selected States' },
                          { value: 'country', label: 'All India' },
                          { value: 'international', label: 'International' },
                        ].map(opt => (
                          <button key={opt.value} type="button"
                            data-testid={`coverage-${opt.value}`}
                            onClick={() => setAdvancedData(prev => ({ ...prev, serviceable_type: opt.value }))}
                            className={`py-2 px-3 text-xs font-medium rounded-lg border-2 transition-all ${
                              advancedData.serviceable_type === opt.value
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {advancedData.serviceable_type === 'state' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Select States</label>
                          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                            {INDIAN_STATES.map(state => (
                              <label key={state} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer text-xs">
                                <input type="checkbox"
                                  checked={advancedData.serviceable_states.includes(state)}
                                  onChange={(e) => {
                                    setAdvancedData(prev => ({
                                      ...prev,
                                      serviceable_states: e.target.checked
                                        ? [...prev.serviceable_states, state]
                                        : prev.serviceable_states.filter(s => s !== state)
                                    }));
                                  }}
                                  className="rounded border-gray-300 text-indigo-600" />
                                {state}
                              </label>
                            ))}
                          </div>
                          {advancedData.serviceable_states.length > 0 && (
                            <p className="text-xs text-indigo-600 mt-1">{advancedData.serviceable_states.length} state(s) selected</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ============ PRODUCT: RETURN & CANCELLATION POLICY ============ */}
                {listingType === 'product' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="return-policy-section">
                    <div className="flex items-center gap-3 mb-3">
                      <ScrollText size={18} className="text-orange-600" />
                      <span className="font-medium text-gray-900">Return & Cancellation Policy</span>
                    </div>
                    <div className="space-y-3">
                      {/* Cancellation */}
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-700">Allow order cancellation</span>
                        <button type="button" data-testid="cancellation-toggle"
                          onClick={() => setAdvancedData(prev => ({ ...prev, cancellation_allowed: !prev.cancellation_allowed }))}
                          className={`w-11 h-6 rounded-full transition-colors ${advancedData.cancellation_allowed ? 'bg-emerald-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.cancellation_allowed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {advancedData.cancellation_allowed && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Cancel Window</label>
                            <select data-testid="cancellation-window-select"
                              value={advancedData.cancellation_window}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, cancellation_window: e.target.value }))}
                              className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                              <option value="2hrs">2 Hours</option>
                              <option value="4hrs">4 Hours</option>
                              <option value="12hrs">12 Hours</option>
                              <option value="24hrs">24 Hours</option>
                              <option value="2days">2 Days</option>
                              <option value="7days">7 Days</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Timing</label>
                            <select data-testid="cancellation-timing-select"
                              value={advancedData.cancellation_timing}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, cancellation_timing: e.target.value }))}
                              className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                              <option value="before_dispatch">Before Dispatch</option>
                              <option value="after_dispatch">After Dispatch</option>
                              <option value="before_delivery">Before Delivery</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {/* Return Policy */}
                      {formData.product_type === 'physical' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Return Policy</label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[
                                { value: 'return_exchange', label: 'Return & Exchange' },
                                { value: 'exchange_only', label: 'Exchange Only' },
                                { value: 'no_return', label: 'No Return' },
                              ].map(opt => (
                                <button key={opt.value} type="button"
                                  data-testid={`return-${opt.value}`}
                                  onClick={() => setAdvancedData(prev => ({ ...prev, return_policy: opt.value }))}
                                  className={`py-2 px-1 text-[10px] font-medium rounded-lg border transition-all ${
                                    advancedData.return_policy === opt.value
                                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                                      : 'border-gray-200 text-gray-500'
                                  }`}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {advancedData.return_policy !== 'no_return' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Return Window</label>
                              <select data-testid="return-window-select"
                                value={advancedData.return_exchange_window}
                                onChange={(e) => setAdvancedData(prev => ({ ...prev, return_exchange_window: e.target.value }))}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                                <option value="7">7 Days</option>
                                <option value="15">15 Days</option>
                                <option value="30">30 Days</option>
                              </select>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ============ SERVICE: LOGISTICS CHARGES ============ */}
                {listingType === 'service' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="logistics-charges-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Truck size={18} className="text-teal-600" />
                        <div>
                          <span className="font-medium text-gray-900 block">Logistics Charges</span>
                          <span className="text-xs text-gray-500">Enable if you charge for visiting/delivery</span>
                        </div>
                      </div>
                      <button type="button" data-testid="logistics-charges-toggle"
                        onClick={() => setAdvancedData(prev => ({ ...prev, has_logistics_charges: !prev.has_logistics_charges }))}
                        className={`w-11 h-6 rounded-full transition-colors ${advancedData.has_logistics_charges ? 'bg-teal-600' : 'bg-gray-300'}`}>
                        <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.has_logistics_charges ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {advancedData.has_logistics_charges && (
                      <div className="space-y-3 border-t border-gray-100 pt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Charge Type</label>
                          <select data-testid="logistics-charge-type"
                            value={advancedData.logistics_charge_type}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, logistics_charge_type: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            <option value="fixed">Fixed Rate</option>
                            <option value="per_km">Per Kilometer</option>
                            <option value="zone_based">Zone Based</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Base Charge (Rs.)</label>
                            <input type="number" data-testid="logistics-base-charge"
                              value={advancedData.logistics_base_charge}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, logistics_base_charge: e.target.value }))}
                              placeholder="0" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                          {advancedData.logistics_charge_type === 'per_km' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Rate/km (Rs.)</label>
                              <input type="number" data-testid="logistics-per-km"
                                value={advancedData.logistics_per_km_rate}
                                onChange={(e) => setAdvancedData(prev => ({ ...prev, logistics_per_km_rate: e.target.value }))}
                                placeholder="0" min="0"
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Free Radius (km)</label>
                            <input type="number" data-testid="logistics-free-radius"
                              value={advancedData.logistics_free_radius}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, logistics_free_radius: e.target.value }))}
                              placeholder="5" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Logistics Terms</label>
                          <textarea data-testid="logistics-description"
                            value={advancedData.logistics_description}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, logistics_description: e.target.value }))}
                            placeholder="e.g., Visiting charges apply beyond 5km..."
                            rows={2}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============ SERVICE: HOLIDAY CALENDAR ============ */}
                {listingType === 'service' && formData.bookingMode !== 'enquiry' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="holiday-calendar-section">
                    <div className="flex items-center gap-3 mb-3">
                      <CalendarOff size={18} className="text-red-500" />
                      <span className="font-medium text-gray-900">Holiday Calendar</span>
                    </div>
                    <HolidayCalendar
                      holidays={advancedData.holidays}
                      onChange={(holidays) => setAdvancedData(prev => ({ ...prev, holidays }))}
                    />
                  </div>
                )}

                {/* ============ SERVICE: SERVICE GUARANTEE ============ */}
                {listingType === 'service' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="service-guarantee-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <ShieldCheck size={18} className="text-green-600" />
                        <span className="font-medium text-gray-900">Service Guarantee</span>
                      </div>
                      <button type="button" data-testid="guarantee-toggle"
                        onClick={() => setAdvancedData(prev => ({ ...prev, service_guarantee_enabled: !prev.service_guarantee_enabled }))}
                        className={`w-11 h-6 rounded-full transition-colors ${advancedData.service_guarantee_enabled ? 'bg-green-600' : 'bg-gray-300'}`}>
                        <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.service_guarantee_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {advancedData.service_guarantee_enabled && (
                      <div className="space-y-3 border-t border-gray-100 pt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Refund Type</label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { value: 'full', label: 'Full Refund' },
                              { value: 'partial', label: 'Partial Refund' },
                            ].map(opt => (
                              <button key={opt.value} type="button"
                                data-testid={`guarantee-${opt.value}`}
                                onClick={() => setAdvancedData(prev => ({ ...prev, service_guarantee_type: opt.value }))}
                                className={`py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
                                  advancedData.service_guarantee_type === opt.value
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-200 text-gray-500'
                                }`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {advancedData.service_guarantee_type === 'partial' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Refund Percentage (%)</label>
                            <input type="number" data-testid="guarantee-pct"
                              value={advancedData.service_guarantee_refund_pct}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, service_guarantee_refund_pct: e.target.value }))}
                              placeholder="e.g., 50" min="1" max="100"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Valid for (days)</label>
                          <input type="number" data-testid="guarantee-days"
                            value={advancedData.service_guarantee_days}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, service_guarantee_days: e.target.value }))}
                            placeholder="e.g., 7" min="1"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============ SERVICE: SERVICEABLE AREA ============ */}
                {listingType === 'service' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="service-serviceable-area-section">
                    <div className="flex items-center gap-3 mb-3">
                      <Globe size={18} className="text-indigo-600" />
                      <div>
                        <span className="font-medium text-gray-900 block">Serviceable Area</span>
                        <span className="text-xs text-gray-500">Where is this service available?</span>
                      </div>
                    </div>
                    {/* Tier 2 warning - if user has kyc_level < 3 */}
                    {user?.vendor_profile?.compliance_tier?.includes('tier_2') && (
                      <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                        Your GST status restricts sales to your registered state. Upgrade to Tier 3 for pan-India sales.
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { value: 'all_india', label: 'All India' },
                        { value: 'selected_states', label: 'Selected States' },
                        { value: 'radius', label: 'Radius (km)' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          data-testid={`service-area-${opt.value}`}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            serviceableArea: { ...prev.serviceableArea, coverage_type: opt.value, states: [], radius_km: '' }
                          }))}
                          className={`py-2 px-2 text-xs font-medium rounded-xl border-2 transition-all ${
                            formData.serviceableArea?.coverage_type === opt.value
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >{opt.label}</button>
                      ))}
                    </div>
                    {formData.serviceableArea?.coverage_type === 'radius' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Radius from your location (km)</label>
                        <input type="number" data-testid="service-area-radius-input"
                          value={formData.serviceableArea?.radius_km || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            serviceableArea: { ...prev.serviceableArea, radius_km: e.target.value }
                          }))}
                          placeholder="e.g., 25" min="1"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                      </div>
                    )}
                    {formData.serviceableArea?.coverage_type === 'selected_states' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">States (comma-separated)</label>
                        <input type="text" data-testid="service-area-states-input"
                          value={(formData.serviceableArea?.states || []).join(', ')}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            serviceableArea: {
                              ...prev.serviceableArea,
                              states: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                            }
                          }))}
                          placeholder="e.g., Maharashtra, Karnataka"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                      </div>
                    )}
                    {formData.serviceableArea?.coverage_type === 'all_india' && (
                      <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                        Available across all of India
                      </p>
                    )}
                  </div>
                )}

                {/* ============ RENTAL: SERVICEABLE AREA (not needed for venue) ============ */}
                {listingType === 'rental' && formData.rental_template !== 'venue_booking' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="rental-serviceable-area-section">
                    <div className="flex items-center gap-3 mb-3">
                      <Globe size={18} className="text-indigo-600" />
                      <div>
                        <span className="font-medium text-gray-900 block">Serviceable Area</span>
                        <span className="text-xs text-gray-500">Where is this rental available?</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { value: 'all_india', label: 'All India' },
                        { value: 'selected_states', label: 'Selected States' },
                        { value: 'radius', label: 'Radius (km)' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          data-testid={`rental-area-${opt.value}`}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            serviceableArea: { ...prev.serviceableArea, coverage_type: opt.value, states: [], radius_km: '' }
                          }))}
                          className={`py-2 px-2 text-xs font-medium rounded-xl border-2 transition-all ${
                            formData.serviceableArea?.coverage_type === opt.value
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >{opt.label}</button>
                      ))}
                    </div>
                    {formData.serviceableArea?.coverage_type === 'radius' && (
                      <input type="number" data-testid="rental-area-radius-input"
                        value={formData.serviceableArea?.radius_km || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          serviceableArea: { ...prev.serviceableArea, radius_km: e.target.value }
                        }))}
                        placeholder="e.g., 25 km" min="1"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                    )}
                    {formData.serviceableArea?.coverage_type === 'selected_states' && (
                      <input type="text" data-testid="rental-area-states-input"
                        value={(formData.serviceableArea?.states || []).join(', ')}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          serviceableArea: {
                            ...prev.serviceableArea,
                            states: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          }
                        }))}
                        placeholder="e.g., Maharashtra, Karnataka"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                    )}
                    {formData.serviceableArea?.coverage_type === 'all_india' && (
                      <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                        Available across all of India
                      </p>
                    )}
                  </div>
                )}

                {/* ============ SERVICE: POLICIES & TERMS ============ */}
                {listingType === 'service' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="service-policies-section">
                    <div className="flex items-center gap-3 mb-3">
                      <ScrollText size={18} className="text-amber-600" />
                      <span className="font-medium text-gray-900">Policies & Terms</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-700">Allow booking cancellation</span>
                        <button type="button" data-testid="svc-cancellation-toggle"
                          onClick={() => setAdvancedData(prev => ({ ...prev, svc_cancellation_allowed: !prev.svc_cancellation_allowed }))}
                          className={`w-11 h-6 rounded-full transition-colors ${advancedData.svc_cancellation_allowed ? 'bg-amber-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.svc_cancellation_allowed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {advancedData.svc_cancellation_allowed && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Cancel Window</label>
                            <select data-testid="svc-cancellation-window"
                              value={advancedData.svc_cancellation_window}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_window: e.target.value }))}
                              className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                              <option value="2hrs">2 Hours</option>
                              <option value="4hrs">4 Hours</option>
                              <option value="8hrs">8 Hours</option>
                              <option value="12hrs">12 Hours</option>
                              <option value="24hrs">24 Hours</option>
                              <option value="2days">2 Days</option>
                              <option value="3days">3 Days</option>
                              <option value="7days">7 Days</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Timing</label>
                            <select data-testid="svc-cancellation-timing"
                              value={advancedData.svc_cancellation_timing}
                              onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_timing: e.target.value }))}
                              className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                              <option value="after_booking">After Booking</option>
                              <option value="before_service_initiation">Before Service Start</option>
                              <option value="before_service_date">Before Service Date</option>
                            </select>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Terms and Conditions</label>
                        <textarea data-testid="svc-terms"
                          value={advancedData.svc_terms}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_terms: e.target.value }))}
                          placeholder="Add terms and conditions..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cancellation Policy Details</label>
                        <textarea data-testid="svc-cancellation-details"
                          value={advancedData.svc_cancellation_details}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_details: e.target.value }))}
                          placeholder="Describe detailed cancellation policy..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ SERVICE: TRAVEL BOOKING SETTINGS ============ */}
                {listingType === 'service' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="service-travel-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <MapPin size={18} className="text-amber-600" />
                        <div>
                          <span className="font-medium text-gray-900 block">Travel Booking</span>
                          <span className="text-xs text-gray-500">Accept out-of-area bookings with travel fee</span>
                        </div>
                      </div>
                      <button type="button" data-testid="allow-travel-booking-toggle"
                        onClick={() => setFormData(prev => ({ ...prev, allow_travel_booking: !prev.allow_travel_booking }))}
                        className={`w-11 h-6 rounded-full transition-colors ${formData.allow_travel_booking ? 'bg-amber-600' : 'bg-gray-300'}`}>
                        <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.allow_travel_booking ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {formData.allow_travel_booking && (
                      <div className="space-y-3 border-t border-gray-100 pt-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Charge per km (₹)</label>
                            <input type="number" data-testid="travel-charge-per-km"
                              value={formData.travel_charge_per_km}
                              onChange={(e) => setFormData(prev => ({ ...prev, travel_charge_per_km: e.target.value }))}
                              placeholder="e.g., 15" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Minimum travel fee (₹)</label>
                            <input type="number" data-testid="min-travel-fee"
                              value={formData.min_travel_fee}
                              onChange={(e) => setFormData(prev => ({ ...prev, min_travel_fee: e.target.value }))}
                              placeholder="e.g., 100" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Max travel distance (km)</label>
                          <input type="number" data-testid="max-travel-distance"
                            value={formData.max_travel_distance_km}
                            onChange={(e) => setFormData(prev => ({ ...prev, max_travel_distance_km: e.target.value }))}
                            placeholder="e.g., 50" min="0"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          <p className="text-xs text-gray-400 mt-1">Leave blank for no limit</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============ RENTAL: TRAVEL BOOKING (not needed for venue) ============ */}
                {listingType === 'rental' && formData.rental_template !== 'venue_booking' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="rental-travel-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <MapPin size={18} className="text-amber-600" />
                        <div>
                          <span className="font-medium text-gray-900 block">Travel / Delivery Booking</span>
                          <span className="text-xs text-gray-500">Accept out-of-area with travel fee</span>
                        </div>
                      </div>
                      <button type="button" data-testid="rental-allow-travel-toggle"
                        onClick={() => setFormData(prev => ({ ...prev, allow_travel_booking: !prev.allow_travel_booking }))}
                        className={`w-11 h-6 rounded-full transition-colors ${formData.allow_travel_booking ? 'bg-amber-600' : 'bg-gray-300'}`}>
                        <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.allow_travel_booking ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {formData.allow_travel_booking && (
                      <div className="space-y-3 border-t border-gray-100 pt-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Charge per km (₹)</label>
                            <input type="number" data-testid="rental-travel-charge-per-km"
                              value={formData.travel_charge_per_km}
                              onChange={(e) => setFormData(prev => ({ ...prev, travel_charge_per_km: e.target.value }))}
                              placeholder="e.g., 15" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Minimum fee (₹)</label>
                            <input type="number" data-testid="rental-min-travel-fee"
                              value={formData.min_travel_fee}
                              onChange={(e) => setFormData(prev => ({ ...prev, min_travel_fee: e.target.value }))}
                              placeholder="e.g., 100" min="0"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Max distance (km)</label>
                          <input type="number" data-testid="rental-max-travel-distance"
                            value={formData.max_travel_distance_km}
                            onChange={(e) => setFormData(prev => ({ ...prev, max_travel_distance_km: e.target.value }))}
                            placeholder="e.g., 50" min="0"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============ RENTAL: POLICIES & TERMS ============ */}
                {listingType === 'rental' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="rental-policies-section">
                    <div className="flex items-center gap-3 mb-3">
                      <ScrollText size={18} className="text-amber-600" />
                      <span className="font-medium text-gray-900">Policies & Terms</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-700">Allow cancellations</span>
                        <button type="button" data-testid="rental-cancellation-toggle"
                          onClick={() => setAdvancedData(prev => ({ ...prev, svc_cancellation_allowed: !prev.svc_cancellation_allowed }))}
                          className={`w-11 h-6 rounded-full transition-colors ${advancedData.svc_cancellation_allowed ? 'bg-amber-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.svc_cancellation_allowed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {advancedData.svc_cancellation_allowed && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Cancellation Window</label>
                          <select data-testid="rental-cancellation-window"
                            value={advancedData.svc_cancellation_window}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_window: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            <option value="2hrs">2 Hours</option>
                            <option value="4hrs">4 Hours</option>
                            <option value="24hrs">24 Hours</option>
                            <option value="48hrs">48 Hours</option>
                            <option value="7days">7 Days</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Damage Policy</label>
                        <textarea data-testid="rental-damage-policy"
                          value={advancedData.svc_cancellation_details}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_details: e.target.value }))}
                          placeholder="Describe what happens in case of damage..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Terms and Conditions</label>
                        <textarea data-testid="rental-terms"
                          value={advancedData.svc_terms}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_terms: e.target.value }))}
                          placeholder="Rental terms and conditions..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ EVENT: POLICIES & TERMS ============ */}
                {listingType === 'event' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="event-policies-section">
                    <div className="flex items-center gap-3 mb-3">
                      <ScrollText size={18} className="text-amber-600" />
                      <span className="font-medium text-gray-900">Refund & Cancellation Policy</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-700">Allow ticket refunds</span>
                        <button type="button" data-testid="event-refund-toggle"
                          onClick={() => setAdvancedData(prev => ({ ...prev, svc_cancellation_allowed: !prev.svc_cancellation_allowed }))}
                          className={`w-11 h-6 rounded-full transition-colors ${advancedData.svc_cancellation_allowed ? 'bg-amber-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.svc_cancellation_allowed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {advancedData.svc_cancellation_allowed && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Refund window</label>
                          <select data-testid="event-refund-window"
                            value={advancedData.svc_cancellation_window}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_window: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            <option value="24hrs">Up to 24 hours before</option>
                            <option value="48hrs">Up to 48 hours before</option>
                            <option value="7days">Up to 7 days before</option>
                            <option value="14days">Up to 14 days before</option>
                            <option value="no_refund">No refunds after booking</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Event Terms & Conditions</label>
                        <textarea data-testid="event-terms"
                          value={advancedData.svc_terms}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_terms: e.target.value }))}
                          placeholder="Enter event terms and conditions..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ SUBSCRIPTION: POLICIES & TERMS ============ */}
                {listingType === 'subscription' && (
                  <div className="p-4 bg-white rounded-xl border border-gray-200" data-testid="subscription-policies-section">
                    <div className="flex items-center gap-3 mb-3">
                      <ScrollText size={18} className="text-amber-600" />
                      <span className="font-medium text-gray-900">Subscription Policies</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-700">Allow cancellations</span>
                        <button type="button" data-testid="subscription-cancellation-toggle"
                          onClick={() => setAdvancedData(prev => ({ ...prev, svc_cancellation_allowed: !prev.svc_cancellation_allowed }))}
                          className={`w-11 h-6 rounded-full transition-colors ${advancedData.svc_cancellation_allowed ? 'bg-amber-600' : 'bg-gray-300'}`}>
                          <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${advancedData.svc_cancellation_allowed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {advancedData.svc_cancellation_allowed && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Cancellation window</label>
                          <select data-testid="subscription-cancellation-window"
                            value={advancedData.svc_cancellation_window}
                            onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_cancellation_window: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            <option value="7days">7 Days notice</option>
                            <option value="14days">14 Days notice</option>
                            <option value="30days">30 Days notice</option>
                            <option value="end_of_cycle">End of billing cycle</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Subscription Terms</label>
                        <textarea data-testid="subscription-terms"
                          value={advancedData.svc_terms}
                          onChange={(e) => setAdvancedData(prev => ({ ...prev, svc_terms: e.target.value }))}
                          placeholder="Minimum commitment, renewal terms..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ SEO SETTINGS ============ */}
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Search size={18} className="text-blue-600" />
                    <span className="font-medium text-gray-900">SEO Settings</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">URL Slug</label>
                      <input type="text"
                        value={advancedData.slug}
                        onChange={(e) => setAdvancedData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                        placeholder="my-product-name"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Meta Title <span className="text-gray-400">({advancedData.meta_title.length}/60)</span>
                      </label>
                      <input type="text"
                        value={advancedData.meta_title}
                        onChange={(e) => setAdvancedData(prev => ({ ...prev, meta_title: e.target.value }))}
                        placeholder={formData.title || "Product title for search engines"}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        maxLength={60} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Meta Description <span className="text-gray-400">({advancedData.meta_description.length}/160)</span>
                      </label>
                      <textarea
                        value={advancedData.meta_description}
                        onChange={(e) => setAdvancedData(prev => ({ ...prev, meta_description: e.target.value }))}
                        placeholder={formData.description?.substring(0, 100) || "Brief description for search results"}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none"
                        maxLength={160} />
                    </div>
                  </div>
                </div>
                
              </div>
            )}
          </div>

          {/* Validation Errors Summary */}
          {validationErrors.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-700 mb-1">Please fix the following:</p>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {validationErrors.map((err, i) => (
                  <li key={err.id || err.name || `${err.title || "err"}-${i}`}>{err.message}</li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100 shrink-0 bg-gray-50/50">
        {/* Upload Progress */}
        {loading && uploadProgress > 0 && (
          <div className="mb-3">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-600 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1 text-center">
              {uploadProgress < 40 ? (editMode ? 'Saving changes...' : 'Submitting listing...') : 
               uploadProgress < 80 ? 'Uploading photos...' : 'Almost done...'}
            </p>
          </div>
        )}
        
        <button 
          onClick={handleSubmit}
          disabled={loading || isLoadingListing}
          data-testid="submit-listing-btn"
          className="w-full py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 disabled:from-gray-400 disabled:to-gray-500 rounded-xl flex items-center justify-center gap-2 text-white font-bold text-lg shadow-lg transition-all"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={22} />
              {editMode ? 'Saving...' : 'Submitting...'}
            </>
          ) : (
            <>
              <CheckCircle size={22} />
              {editMode ? 'Save Changes' : 'Submit for Review'}
              <ChevronRight size={18} className="ml-1" />
            </>
          )}
        </button>
        
        <p className="text-center text-xs text-gray-500 mt-3">
          {editMode 
            ? '📋 Your changes will be submitted for review'
            : '📋 Your listing will be reviewed by admin before it\'s visible to customers'
          }
        </p>
      </div>
    </div>
  );
};

export default LiteAddItemOverlay;
